import ErrorBoundary from '../ui/ErrorBoundary'
import { clampBoardName } from '../lib/text'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import ItemDetailPanel from '../board/ItemDetailPanel'
import LabelsEditor from '../board/LabelsEditor'
import FormulaEditor from '../board/FormulaEditor'
import ProgressSettings from '../board/ProgressSettings'
import NumberFormatDialog from '../board/NumberFormatDialog'
import BoardSettingsPanel from '../board/BoardSettingsPanel'
import ArchivePanel from '../board/ArchivePanel'
import { boardToCsv, boardToJson, downloadText, safeFileName } from '../board/export'
import { boardToXlsxBlob, downloadBlob } from '../board/exportXlsx'
import { parseMondayXlsx } from '../board/mondayImport'
import { AlignIcon } from '../board/AlignIcons'
import ColumnCenter from '../board/ColumnCenter'
import { IMPLEMENTED_COLUMN_TYPES, columnTypeLabel } from '../board/columnTypes'
import { computeDerivedValues } from '../board/derived'
import { applyRowAndHeaderHeight } from '../board/geometry'
import { setEditLabelsHandler, setTagPool } from '../board/BoardCell'
import { labelTable } from '../board/BoardCell'
import { ApiError, convertColumnType, createColumn, createGroup, createItem, createItemBelow, duplicateGroup, archiveBoard, archiveItem, copyBoard, deleteBoard, duplicateItem, renameBoard, moveItem, patchBoardSettings, updateColumnLabels, deleteColumn, deleteGroup, deleteItem, getBoard, patchCell, reorder, updateColumn, updateGroup , setFooterAgg, importIntoBoard, setBoardAppSetting } from '../api/client'
import type { BoardResponse, CellPatchResponse, ColumnInfo, GroupInfo, ItemInfo } from '../api/client'
import { CellView } from '../board/BoardCell'
import { colorOf, GROUP_COLOR_NAMES, nextGroupColor } from '../board/palette'
import { applyGeometryVars } from '../board/geometry'
import { askConfirm, askPrompt, askSelect } from '../board/Dialog'
import { DialogHost } from '../board/Dialog'

// 几何常量写进 CSS 变量（单一数据源在 board/geometry.ts，对齐 spec §3）
/**
 * 1×1 透明图，用作 setDragImage 的"假拖影"。
 *
 * 为什么需要：浏览器默认拿被拖元素（组头）做半透明快照跟着指针走，
 * 而我们要的是自己画的 .group-ghost 卡片 —— 两者同时出现就会看到"两张卡"。
 * 设成透明图后，原生拖影不可见，只留我们的浮层。
 */
let transparentDragImage: HTMLImageElement | null = null
function getTransparentDragImage(): HTMLImageElement {
  if (transparentDragImage !== null) return transparentDragImage
  const img = new Image()
  img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
  transparentDragImage = img
  return img
}

applyGeometryVars()

type Editing = { itemId: number; columnKey: string } | null

/** 写入成功后就地更新，省掉一次整板往返；rev 换成服务端返回的新值 */
function applyCell(board: BoardResponse, res: CellPatchResponse): BoardResponse {
  return {
    ...board,
    board: { ...board.board, rev: res.rev },
    items: board.items.map((it) => {
      if (it.id !== res.itemId) return it
      if (res.columnKey === 'name') return { ...it, name: res.value }
      return { ...it, values: { ...it.values, [res.columnKey]: res.value } }
    }),
  }
}

/** M8：从 Board.ViewState 里取「默认条目取值」（columnKey -> 值） */
function readItemDefaults(raw: unknown): Record<string, string> {
  let vs: Record<string, unknown> = {}
  try {
    const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (parsed !== null && typeof parsed === 'object') vs = parsed as Record<string, unknown>
  } catch {
    vs = {}
  }
  const d = vs.DefaultItemValues
  if (d === null || typeof d !== 'object') return {}
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(d as Record<string, unknown>)) {
    if (typeof v === 'string') out[k] = v
  }
  return out
}
/** M10：由列自身布局决定的列类型 —— 这些列不吃看板级对齐设置 */
const ALIGN_LOCKED: Record<string, string | null> = {
  formula: 'center',            // 公式居中
  boolean: 'center',            // 复选框居中
  tag: 'center',                // 标签居中（用户指定）
  timerange: 'center',          // 时间线固定居中（用户 2026-10-21 指定）
  rating: 'center',             // 评分固定居中（用户 2026-10-21 指定）
  color: null,                  // 色块自身铺满
  dropdown: null,               // 色块自身铺满
  'columns-battery': null,      // 进度条铺满
}

/** 这些列在列菜单里不显示「对齐方式」 */
const alignLocked = (type: string): boolean => type in ALIGN_LOCKED

function readColumnSettings(col: { settings: unknown }): Record<string, unknown> {
  const raw = col.settings
  if (raw !== null && typeof raw === 'object') return raw as Record<string, unknown>
  if (typeof raw === 'string' && raw !== '') {
    try {
      const p: unknown = JSON.parse(raw)
      if (p !== null && typeof p === 'object') return p as Record<string, unknown>
    } catch {
      /* 非法 JSON：当空设置 */
    }
  }
  return {}
}

/** 某列最终的对齐：列 settings.align > 看板 uiSettings.columnAlign > left；锁定列返回其固定值 */
function columnAlign(col: ColumnInfo, uiSettings: Record<string, string>): string | null {
  if (alignLocked(col.type)) return ALIGN_LOCKED[col.type] ?? null
  const own = readColumnSettings(col).align
  if (own === 'left' || own === 'center' || own === 'right') return own
  const g = uiSettings.columnAlign
  return g === 'center' || g === 'right' ? g : 'left'
}


function GroupBlock({
  gId,
  group,
  columns,
  items,
  editing,
  busy,
  onStartEdit,
  onCommit,
  onCancel,
  onDropItem,
  onDropToGroup,
  onDropColumn,
  onDropGroup,
  onCreateItem,
  onDeleteItem,
  colOps,
  grpOps,
  selected,
  onToggleSel,
  onToggleGroup,
  onDuplicateItem,
  onCreateBelow,
  onOpenItem,
  onOpenItemNewTab,
  onCopyLink,
  onMoveItem,
  otherGroups,
  pinnedCount,
  onArchiveItem,
  footerStatMode,
  onCycleAgg,
  uiSettings,
  groupDrop,
  onSetGroupDrop,
  compact,
  onDragStartGroup,
  onDragEndGroup,
  dragDy,
  shift,
  lift,
}: {
  gId: number
  group: GroupInfo
  /** 当前的分组落点（由页面统一持有，避免每个组各自记一份） */
  groupDrop: { id: number; after: boolean } | null
  onSetGroupDrop: (drop: { id: number; after: boolean } | null) => void
  /** 正被拖动：只留组头，表格与新增行都收起（拖动行为由页面上的浮层卡片负责） */
  compact: boolean
  onDragStartGroup: (info: { id: number; title: string; count: number; color: string; width: number; startY: number; barHeight: number; sectionHeight: number }) => void
  onDragEndGroup: () => void
  /**
   * 被拖分组跟手的纵向位移（px）。实测 monday 不建幽灵节点：
   * 折叠后把源节点自身用 transform 抬起来跟手（ANALYSIS-13 §1.2）。
   */
  dragDy: number
  /** 其它分组让位：负数上移、正数下移（px） */
  shift: number
  /** 折叠后源组被顶上去的距离，用 transform 补回 */
  lift: number
  columns: ColumnInfo[]
  items: ItemInfo[]
  editing: Editing
  busy: boolean
  onStartEdit: (itemId: number, columnKey: string) => void
  onCommit: (itemId: number, columnKey: string, value: string) => void
  onCancel: () => void
  onDropItem: (targetItemId: number, after: boolean) => void
  onDropColumn: (targetColumnId: number, after: boolean) => void
  onDropGroup: (targetGroupId: number, after: boolean) => void
  onCreateItem: (name: string) => void
  onDeleteItem: (itemId: number) => void
  onDropToGroup: () => void
  colOps: {
    rename: (id: number, title: string) => void
    remove: (id: number) => void
    resize: (id: number, width: number) => void
    /** 复制列（只复制 key/title/type/width） */
    duplicate: (id: number) => void
    /** 在右侧插入列 */
    insertRight: (id: number) => void
    /** 改变列类型 */
    changeType: (id: number, current: string) => void
    /** 编辑标签（列设置） */
    editLabels: (id: number) => void
  editFormula: (id: number) => void
  editProgress: (id: number) => void
  editNumFormat: (id: number) => void
  setAlign: (id: number, align: string) => void
  }
  grpOps: {
    rename: (title: string) => void
    remove: () => void
    /** 导出本组为 CSV */
    export: () => void
    /** 复制分组（连带条目与值） */
    duplicate: () => void
    /** 改分组颜色（传色名） */
    recolor: (color: string) => void
    /** 折叠 / 展开本组 */
    toggleCollapse: () => void
  }
  /** M4：勾选状态由 BoardPage 持有，这里只读 */
  selected: Set<number>
  onToggleSel: (itemId: number) => void
  onToggleGroup: (itemIds: number[]) => void
  /** 复制条目（带值） */
  onDuplicateItem: (itemId: number) => void
  /** 在指定条目下方新建 */
  onCreateBelow: (itemId: number) => void
  /** 打开条目详情 */
  onOpenItem: (itemId: number) => void
  /** 新标签页打开 */
  onOpenItemNewTab: (itemId: number) => void
  /** 复制条目链接 */
  onCopyLink: (itemId: number) => void
  /** 移动到指定分组 */
  onMoveItem: (itemId: number, toGroupId: number) => void
  /** 可供移动的目标分组 */
  otherGroups: { id: number; title: string }[]
  /** M8：名称列之后额外冻结的列数 */
  pinnedCount: number
  /** M8：归档条目（从看板隐藏，可在归档视图恢复） */
  onArchiveItem: (itemId: number) => void
  footerStatMode: Record<string, string>
  uiSettings: Record<string, string>
  onCycleAgg: (columnKey: string) => void
}) {
  const sorted = [...items].sort((a, b) => a.pos - b.pos)
  // 新增条目的输入框内容，随组各自独立
  const [draft, setDraft] = useState('')
  // 当前展开行菜单的条目 id（同一时刻只开一个）
  const [menuFor, setMenuFor] = useState<number | null>(null)
  // 当前展开的列头菜单：位置用 fixed 坐标，因为列头在横滚容器里，absolute 会被裁掉
  const [colMenu, setColMenu] = useState<{ id: number; x: number; y: number } | null>(null)
  // 就地编辑：正在改标题的列 / 分组（spec §13.1：列头标题就地编辑 input[maxlength=255]）
  const [renamingCol, setRenamingCol] = useState<number | null>(null)
  const [renamingGroup, setRenamingGroup] = useState(false)
  // 分组 ⋯ 菜单
  // 颜色选择器是否展开
  const [colorPick, setColorPick] = useState(false)
  // 分组 ⋯ 菜单：按按钮实测坐标定位（钉在条的右边缘会跑偏）
  const [grpMenu, setGrpMenu] = useState<{ x: number; y: number } | null>(null)
  // 条目「移动到」的展开状态
  const [moveFor, setMoveFor] = useState<number | null>(null)
  // 拖动列宽时的即时预览（松手才提交）
  const [resizeW, setResizeW] = useState<{ id: number; w: number } | null>(null)
  // M8：冻结列的实测左偏移（按列 id），由 useLayoutEffect 量出
  const [pinLeft, setPinLeft] = useState<Record<number, number>>({})
  // M8：量出冻结列的自然左偏移。用真实布局值（table 自动布局会改变列宽），
  // 并且把当前 scrollLeft 加回去，保证拿到的是「未滚动时相对滚动容器的左偏移」。
  useLayoutEffect(() => {
    if (pinnedCount <= 0) {
      setPinLeft((prev) => (Object.keys(prev).length === 0 ? prev : {}))
      return
    }
    const scroll = document.querySelector('.btable-scroll')
    const ths = [...document.querySelectorAll('.btable thead th[data-col-id]')]
    if (scroll === null || ths.length === 0) return
    const base = scroll.getBoundingClientRect().left - scroll.scrollLeft
    const next: Record<number, number> = {}
    // 名称列已由 CSS 冻结，不参与本功能的计数与偏移
    const cand = ths.filter((th) => th.getAttribute('data-col-key') !== 'name')
    const limit = Math.min(pinnedCount, cand.length)
    for (let i = 0; i < limit; i += 1) {
      const th = cand[i]
      if (th === undefined) break
      const colId = Number(th.getAttribute('data-col-id'))
      if (!Number.isFinite(colId)) continue
      next[colId] = Math.round(th.getBoundingClientRect().left - base)
    }
    setPinLeft(next)
  }, [pinnedCount, columns, resizeW, group.collapsed])
  // 第一列是**固定名称列**（key === 'name')：始终最前、不可拖动、不可删除，
  // 内容就是"这一行是什么"。其余列按 pos 排。
  // ===== 组尾聚合：只对 color（状态）与 dropdown（阶段）两类列统计标签分布 =====
  // 其余列类型在 monday 里也不聚合（显示为空），所以这里一律留空。
  // M9.5：数值类（numeric / rating）与布尔类（boolean）在组尾给统计
  const footerNum = new Map<number, { count: number; sum: number; avg: number; min: number; max: number }>()
  // 布尔列单独给「已勾选数」
  const footerBool = new Map<number, { checked: number; total: number }>()
  // M9.5：进度列（columns-battery）的组尾统计 —— 取平均值
  const footerBattery = new Map<number, { count: number; avg: number }>()

  // M9.6：派生列（进度 + 公式）一次算好，行渲染与组尾统计都用它
  const derived = computeDerivedValues(columns, sorted)

  const footerAgg = new Map<number, { label: string; n: number; color: string }[]>()
  const footerTotal = new Map<number, number>()
  for (const c of columns) {
    if (c.type !== 'color' && c.type !== 'dropdown') continue
    const { text, color } = labelTable(c)
    const counts = new Map<string, number>()
    for (const it of sorted) {
      const v = it.values[c.key]
      if (v === undefined || v === '') continue
      counts.set(v, (counts.get(v) ?? 0) + 1)
    }
    if (counts.size === 0) continue
    const segs = [...counts.entries()]
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([k, n]) => ({ label: text[k] ?? k, n, color: color[k] ?? '#c4c4c4' }))
    footerAgg.set(c.id, segs)
    footerTotal.set(c.id, segs.reduce((sum, x) => sum + x.n, 0))
  }
  // M9.5：数值类（numeric / rating）与布尔类（boolean）的组尾统计
  for (const c of columns) {
    if (c.type !== 'numeric' && c.type !== 'rating' && c.type !== 'boolean' && c.type !== 'formula') continue
    if (c.type === 'boolean') {
      let checked = 0
      let total = 0
      for (const it of sorted) {
        const v = (it.values[c.key] ?? '').trim()
        if (v === '') continue
        total += 1
        if (v === 'true' || v === '1') checked += 1
      }
      if (total > 0) footerBool.set(c.id, { checked, total })
      continue
    }
    let count = 0
    let sum = 0
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const it of sorted) {
      const raw = ((derived.get(it.id)?.[c.key] ?? it.values[c.key]) ?? '').trim()
      if (raw === '') continue
      const n = Number(raw)
      if (!Number.isFinite(n)) continue
      count += 1
      sum += n
      if (n < min) min = n
      if (n > max) max = n
    }
    if (count > 0) footerNum.set(c.id, { count, sum, avg: sum / count, min, max })
  }
  // M9.5：进度列取平均（只统计有值的条目）
  for (const c of columns) {
    if (c.type !== 'columns-battery') continue
    let count = 0
    let sum = 0
    for (const it of sorted) {
      const raw = ((derived.get(it.id)?.[c.key] ?? it.values[c.key]) ?? '').trim()
      if (raw === '') continue
      const n = Number(raw)
      if (!Number.isFinite(n)) continue
      count += 1
      sum += n
    }
    if (count > 0) footerBattery.set(c.id, { count, avg: sum / count })
  }
  /** M11-2：列拖动的落点，用于画插入提示线 */
  const [colDrop, setColDrop] = useState<{ id: number; after: boolean } | null>(null)
  /** M11-2：行拖动的落点（蓝线提示） */
  const [rowDrop, setRowDrop] = useState<{ id: number; after: boolean } | null>(null)
  /** M11-2：分组拖动的落点 + 自身是否在拖（card 幽灵观感） */
  const [selfDragging, setSelfDragging] = useState(false)


  const orderedColumns = [...columns].sort((a, b) => {
    const an = a.key === 'name' ? 0 : 1
    const bn = b.key === 'name' ? 0 : 1
    return an - bn || a.pos - b.pos
  })

  return (
    <section
        data-group-id={gId}
        style={
          selfDragging && dragDy !== 0
            ? { transform: `translateY(${dragDy + lift}px)` }
            : shift !== 0
              ? { transform: `translateY(${shift}px)` }
            : undefined
        }
        className={
          'group' +
          (groupDrop !== null && groupDrop.id === gId
            ? groupDrop.after ? ' group--drop-after' : ' group--drop-before'
            : '') +
          (selfDragging ? ' group--dragging' : '') +
          (compact ? ' group--compact' : '') +
          (!selfDragging && shift !== 0 ? ' group--shifting' : '')
        }
      onDragOver={(e) => {
        if (draggingGroupId === null && draggingItemId === null) return
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        // 拖分组时：指针在容器的上半部分=插到该组之前，下半部分=插到之后。
        // 组与组之间的空隙算在上一组的盒子里，所以那段落脚处也走这里。
        if (draggingGroupId !== null) {
          const r = e.currentTarget.getBoundingClientRect()
          dropAfter = (e.clientY - r.top) > r.height / 2
          onSetGroupDrop({ id: gId, after: dropAfter })
        }
      }}
      onDrop={(e) => {
        e.preventDefault()
        if (draggingGroupId !== null) {
          onDropGroup(gId, dropAfter)
          return
        }
        if (draggingItemId !== null) onDropToGroup()
      }}
    >
      <div
        className={'group__bar' + (group.collapsed ? ' group__bar--collapsed' : '')}
        style={{ background: colorOf(group.color) }}
        draggable={!busy}
        onDragStart={(e) => {
          draggingGroupId = gId
          setSelfDragging(true)
          e.dataTransfer.effectAllowed = 'move'
          // 用透明图顶掉浏览器自带拖影，保证只看到我们的浮层卡片
          e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0)
          // 拖动期间真正的"跟手卡片"是页面级浮层；这里只上报必要信息
          const bar = e.currentTarget as HTMLElement
          onDragStartGroup({
          startY: e.clientY,
          barHeight: bar.getBoundingClientRect().height,
          sectionHeight: (bar.closest('.group') as HTMLElement).getBoundingClientRect().height,
            id: gId,
            title: group.title,
            count: items.length,
            color: colorOf(group.color),
            width: Math.max(320, bar.getBoundingClientRect().width),
          })
        }}
        onDragEnd={() => {
          draggingGroupId = null
          setSelfDragging(false)
          onSetGroupDrop(null)
          onDragEndGroup()
        }}
      >
        {renamingGroup ? (
          <input
            className="title-input title-input--group"
            maxLength={255}
            defaultValue={group.title}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = e.currentTarget.value.trim()
                setRenamingGroup(false)
                if (v !== '' && v !== group.title) grpOps.rename(v)
              } else if (e.key === 'Escape') {
                setRenamingGroup(false)
              }
            }}
            onBlur={(e) => {
              const v = e.currentTarget.value.trim()
              setRenamingGroup(false)
              if (v !== '' && v !== group.title) grpOps.rename(v)
            }}
          />
        ) : (
          <span className="group__title" onDoubleClick={() => { if (busy) return; setRenamingGroup(true) }} title="双击改名">
            {group.title}
          </span>
        )}
        <button
          type="button"
          className="group-collapse-btn"
          disabled={busy}
          aria-label={group.collapsed ? 'Expand group items' : 'Collapse group items'}
          title={group.collapsed ? '展开本组' : '折叠本组'}
          onClick={() => grpOps.toggleCollapse()}
        >
          {group.collapsed ? '▸' : '▾'}
        </button>
        <span className="group__count">{sorted.length}</span>
        {/* 组级全选：monday 的表头全选是"按分组"语义 */}
        <input
          type="checkbox"
          className="group-check"
          checked={sorted.length > 0 && sorted.every((it) => selected.has(it.id))}
          disabled={busy || sorted.length === 0}
          aria-label="Unselect all items in this group"
          title="全选 / 取消本组"
          onChange={() => onToggleGroup(sorted.map((it) => it.id))}
        />
                <button
          type="button"
          className="group-menu-btn"
          title="分组操作"
          disabled={busy}
          onClick={(e) => {
            if (grpMenu !== null) { setGrpMenu(null); return }
            const r = e.currentTarget.getBoundingClientRect()
            setGrpMenu({ x: r.left, y: r.bottom + 4 })
          }}
        >
          ⋯
        </button>
        {grpMenu !== null && (
          <>
            <div className="row-menu-backdrop" onClick={() => { setGrpMenu(null); setColorPick(false) }} />
            <div className="group-menu" style={{ left: grpMenu.x, top: grpMenu.y }}>
              <button
                type="button"
                className="row-menu__item"
                disabled={busy}
                onClick={() => { setGrpMenu(null); grpOps.toggleCollapse() }}
              >
                {group.collapsed ? '展开本组' : '折叠本组'}
              </button>
              <button type="button" className="row-menu__item" disabled title="M5：分组折叠">
                折叠全部分组<span className="mi__m">M5</span>
              </button>
              <div className="mi__sep" />
              <button
                type="button"
                className="row-menu__item"
                disabled={busy || sorted.length === 0}
                onClick={() => {
                  setGrpMenu(null)
                  onToggleGroup(sorted.map((x) => x.id))
                }}
              >
                全选本组条目（{sorted.length}）
              </button>
              <button type="button" className="row-menu__item" disabled={busy} onClick={() => { setGrpMenu(null); grpOps.duplicate() }}>
                复制分组
              </button>
              <button type="button" className="row-menu__item" disabled title="移动到其它看板，等多看板版本">
                移动分组<span className="mi__m">预留</span>
              </button>
              <button type="button" className="row-menu__item" onClick={() => { setGrpMenu(null); setRenamingGroup(true) }}>
                重命名分组
              </button>
              <button
                type="button"
                className="row-menu__item"
                onClick={() => setColorPick(true)}
              >
                修改分组颜色
              </button>
              {colorPick && (
                <div className="color-pop">
                  <div className="row-menu__head">分组颜色</div>
                  <div className="color-grid">
                    {GROUP_COLOR_NAMES.map((name) => (
                      <button
                        key={name}
                        type="button"
                        className={'color-swatch' + (name === group.color ? ' color-swatch--on' : '')}
                        style={{ background: colorOf(name) }}
                        title={name}
                        aria-label={name}
                        onClick={() => { setColorPick(false); setGrpMenu(null); grpOps.recolor(name) }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <button type="button" className="row-menu__item" onClick={() => { setGrpMenu(null); grpOps.export() }}>
                导出本组为 CSV
              </button>
              <div className="mi__sep" />
              <button
                type="button"
                className="row-menu__item row-menu__item--danger"
                onClick={() => {
                  setGrpMenu(null)
                  void askConfirm('删除分组？', `删除分组「${group.title}」？该组下的 ${sorted.length} 个条目会一起删除。`, '删除').then((ok2) => { if (ok2) grpOps.remove() })
                }}
              >
                删除分组
              </button>
            </div>
          </>
        )}
      </div>

      {/* 组卡片的兄弟节点：左侧竖轨。行 ⋮ 在这里 —— 它在卡片之外，
          因此永远不会被卡片的横滚容器裁掉，视觉上也和 monday 一致。 */}
      <div className={'group__body' + (group.collapsed ? ' group__body--collapsed' : '')}>
        <div className="group__rail">
          <div className="rail__spacer" />
          {sorted.map((it) => (
            <div key={it.id} className="rail__slot">
              <button
                type="button"
                className="row-menu-btn"
                title="更多操作"
                disabled={busy}
                onClick={() => { setMoveFor(null); setMenuFor(menuFor === it.id ? null : it.id) }}
              >
                ⋯
              </button>
              {menuFor === it.id && (
                <div className="row-menu-backdrop" onClick={() => { setMenuFor(null); setMoveFor(null) }} />
              )}
              {menuFor === it.id && (
                                  <div
                    className="row-menu"
                    onMouseOver={(e) => {
                      const t = e.target as HTMLElement
                      // 在二级面板里 → 保留
                      if (t.closest('.row-menu--sub') !== null) return
                      // 指针落在容器背景/分隔线上（不是菜单项）→ 不清，
                      // 否则慢速移动时经过这些区域就会把二级关掉
                      const item = t.closest('.row-menu__item')
                      if (item === null) return
                      // 落在「移动到」这一项上 → 保留
                      if ((item as HTMLElement).dataset.sub === '1') return
                      setMoveFor(null)
                    }}
                  >
                    <button type="button" className="row-menu__item" onClick={() => { setMenuFor(null); onOpenItem(it.id) }}>
                      打开条目
                    </button>
                    <button type="button" className="row-menu__item" onClick={() => { setMenuFor(null); onOpenItemNewTab(it.id) }}>
                      在新标签页打开
                    </button>
                    <button type="button" className="row-menu__item" onClick={() => { setMenuFor(null); onCopyLink(it.id) }}>
                      复制链接
                    </button>
                    <div className="mi__sep" />
                    <button
                      type="button"
                      className="row-menu__item"
                      data-sub="1"
                      onMouseEnter={() => setMoveFor(it.id)}
                      onClick={() => setMoveFor(moveFor === it.id ? null : it.id)}
                    >
                      移动到 ▸
                    </button>
                    {moveFor === it.id && (
                      <div className="row-menu row-menu--sub" onMouseLeave={() => setMoveFor(null)}>
                        <div className="row-menu__head">移动到分组</div>
                        {otherGroups.filter((g) => g.id !== it.groupId).map((g) => (
                          <button
                            key={g.id}
                            type="button"
                            className="row-menu__item"
                            onClick={() => { setMoveFor(null); setMenuFor(null); onMoveItem(it.id, g.id) }}
                          >
                            {g.title}
                          </button>
                        ))}
                        {otherGroups.filter((g) => g.id !== it.groupId).length === 0 && (
                          <span className="row-menu__empty">没有其它分组</span>
                        )}
                      </div>
                    )}                    <button type="button" className="row-menu__item" disabled={busy} onClick={() => { setMenuFor(null); onDuplicateItem(it.id) }}>
                      复制条目
                    </button>
                    <button type="button" className="row-menu__item" disabled={busy} onClick={() => { setMenuFor(null); onCreateBelow(it.id) }}>
                      在下方新建
                    </button>
                    <div className="mi__sep" />
                    {/* M8：归档条目 */}
                    {/* M8：归档条目 */}
                    <button
                      type="button"
                      className="row-menu__item"
                      disabled={busy}
                      onClick={() => { setMenuFor(null); onArchiveItem(it.id) }}
                    >
                      归档条目
                    </button>
                    <button
                      type="button"
                      className="row-menu__item row-menu__item--danger"
                      onClick={() => {
                        setMenuFor(null)
                        void askConfirm('删除条目？', `删除条目「${it.name}」？此操作不可撤销。`, '删除').then((r) => { if (r) onDeleteItem(it.id) })
                      }}
                    >
                      删除条目
                    </button>
                  </div>
              )}
            </div>
          ))}
        </div>

        <div className="group__card">
      <div className="btable-scroll">
        <table className="btable">
        <thead>
          <tr>
              <th className="row-handle-col" aria-hidden="true" />
            {orderedColumns.map((c) => (
                            <th
                              data-col-id={c.id}
                              data-col-key={c.key}
                key={c.id}
                style={{
                  width: resizeW?.id === c.id ? resizeW.w : c.width,
                  ...pinStyle(c, pinLeft),
                }}
                className={
                  colDrop === null || colDrop.id !== c.id
                    ? undefined
                    : colDrop.after
                      ? 'col-drop-after'
                      : 'col-drop-before'
                }
                onDragOver={(e) => {
                  if (draggingColumnId === null) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                  const r = e.currentTarget.getBoundingClientRect()
                  dropAfter = (e.clientX - r.left) > r.width / 2
                  setColDrop({ id: c.id, after: dropAfter })
                }}
                onDragLeave={() => {
                  if (draggingColumnId !== null) setColDrop(null)
                }}
                onDrop={(e) => {
                  if (draggingColumnId === null) return
                  e.preventDefault()
                  e.stopPropagation()
                  setColDrop(null)
                  onDropColumn(c.id, dropAfter)
                }}
              >
                <span
                  className={c.key === 'name' ? 'col-drag col-drag--hidden' : 'col-drag'}
                  draggable={!busy}
                  title="拖动调整列顺序"
                  onDragStart={(e) => {
                    // 固定名称列不可拖动
                    if (c.key === 'name') {
                      e.preventDefault()
                      return
                    }
                    draggingColumnId = c.id
                    e.dataTransfer.effectAllowed = 'move'
          // 用透明图顶掉浏览器自带拖影，保证只看到我们的浮层卡片
          e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0)
                    // 默认只有这个柄会被当成拖动虚影，看起来不像「拖一列」；换成整列头
                    const th = e.currentTarget.closest('th')
                    if (th !== null) e.dataTransfer.setDragImage(th, th.clientWidth / 2, th.clientHeight / 2)
                  }}
                  onDragEnd={() => { draggingColumnId = null; setColDrop(null) }}
                >
                  ⋮⋮
                </span>
                {renamingCol === c.id ? (
                  <input
                    className="title-input"
                    maxLength={255}
                    defaultValue={c.title}
                    autoFocus
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const v = e.currentTarget.value.trim()
                        setRenamingCol(null)
                        if (v !== '' && v !== c.title) colOps.rename(c.id, v)
                      } else if (e.key === 'Escape') {
                        setRenamingCol(null)
                      }
                    }}
                    onBlur={(e) => {
                      const v = e.currentTarget.value.trim()
                      setRenamingCol(null)
                      if (v !== '' && v !== c.title) colOps.rename(c.id, v)
                    }}
                  />
                ) : (
                  <span className="col-title" onDoubleClick={() => { if (busy) return; setRenamingCol(c.id) }} title="双击改名">
                    {c.title}
                  </span>
                )}
                <span
                  className="col-resize"
                  title="拖动调整列宽"
                  onMouseDown={(e) => {
                    // 归档看板只读：busy 已包含 readOnly，直接不启动拖动
                    if (busy) return
                    e.preventDefault()
                    e.stopPropagation()
                    const startX = e.clientX
                    const startW = c.width
                    let latest = startW
                    const onMove = (ev: MouseEvent) => {
                      latest = Math.max(80, Math.round(startW + ev.clientX - startX))
                      // 即时预览：只改本地宽度，不发请求
                      setResizeW({ id: c.id, w: latest })
                    }
                    const onUp = () => {
                      window.removeEventListener('mousemove', onMove)
                      window.removeEventListener('mouseup', onUp)
                      setResizeW(null)
                      // 只在松手时提交一次，避免拖动过程中每帧 PATCH 造成 rev 风暴
                      if (latest !== startW) colOps.resize(c.id, latest)
                    }
                    window.addEventListener('mousemove', onMove)
                    window.addEventListener('mouseup', onUp)
                  }}
                />
                {/* 名称列没有 ⋯ 按钮：整个触发键与菜单都在这里被排除（不是靠菜单项里的 display:none）。 */}
                {c.key !== 'name' && (
                  <>
                    <button
                      type="button"
                      className="col-menu-btn"
                      title="列操作"
                      disabled={busy}
                      onClick={(e) => {
                        if (colMenu?.id === c.id) {
                          setColMenu(null)
                          return
                        }
                        const r = e.currentTarget.getBoundingClientRect()
                        setColMenu({ id: c.id, x: r.right - 150, y: r.bottom + 4 })
                      }}
                    >
                      ⋯
                    </button>
                    {colMenu?.id === c.id && (
                      <div className="col-menu-backdrop" onClick={() => setColMenu(null)} />
                    )}
                    {colMenu?.id === c.id && (
                                            <div className="col-menu" style={{ left: colMenu.x, top: colMenu.y }}>
                        <button
                          type="button"
                          className="col-menu__item"
                          onClick={() => {
                            setColMenu(null)
                            setRenamingCol(c.id)
                          }}
                        >
                          重命名列
                        </button>
                        <button
                          type="button"
                          className="col-menu__item"
                          disabled={busy}
                          onClick={() => {
                            setColMenu(null)
                            colOps.duplicate(c.id)
                          }}
                        >
                          复制列
                        </button>
                        <button type="button" className="col-menu__item" disabled={busy} onClick={() => { setColMenu(null); colOps.insertRight(c.id) }}>
                          在右侧插入列
                        </button>
                        <button type="button" className="col-menu__item" disabled={busy} onClick={() => { setColMenu(null); colOps.changeType(c.id, c.type) }}>
                          改变列类型
                        </button>
<button type="button" className="col-menu__item" style={{ display: c.type !== 'color' && c.type !== 'dropdown' ? 'none' : undefined }} disabled={busy} onClick={() => { setColMenu(null); colOps.editLabels(c.id) }}>
                          编辑标签
                        </button>
<button type="button" className="col-menu__item" style={{ display: c.type !== 'formula' ? 'none' : undefined }} disabled={busy} onClick={() => { setColMenu(null); colOps.editFormula(c.id) }}>
  编辑公式
</button>
<button type="button" className="col-menu__item" style={{ display: c.type !== 'columns-battery' ? 'none' : undefined }} disabled={busy} onClick={() => { setColMenu(null); colOps.editProgress(c.id) }}>
  设置进度
</button>
                          {!alignLocked(c.type) && (
                            <>
                              <div className="mi__sep" />
                              <div className="col-menu__block">
                                <div className="col-menu__block-title">对齐方式</div>
                                <div className="align-row">
                                  {([
                                    ['', '跟随全局'],
                                    ['left', '左'],
                                    ['center', '中'],
                                    ['right', '右'],
                                  ] as const).map(([v, label]) => (
                                    <button
                                      key={v}
                                      type="button"
                                      className={'align-btn' + (String(readColumnSettings(c).align ?? '') === v ? ' is-on' : '')}
                                      disabled={busy}
                                      title={label}
                                      aria-label={label}
                                      onClick={() => {
                                        const cur = String(readColumnSettings(c).align ?? '')
                                        setColMenu(null)
                                        if (cur !== v) colOps.setAlign(c.id, v)
                                      }}
                                    >
                                      <AlignIcon kind={v === '' ? 'auto' : (v as 'left' | 'center' | 'right')} />
                                    </button>
                                  ))}
                                </div>
                              </div>
                            </>
                          )}
                        <div className="mi__sep" />
                        {c.type === 'formula' && (
                          <button
                            type="button"
                            className="col-menu__item"
                            disabled={busy}
                            onClick={() => {
                              setColMenu(null)
                              colOps.editNumFormat(c.id)
                            }}
                          >
                            显示格式…
                          </button>
                        )}
                        <div className="mi__sep" />
                        <button
                          type="button"
                          className="col-menu__item col-menu__item--danger"
                          onClick={() => {
                            setColMenu(null)
                            void askConfirm('删除列？', `删除列「${c.title}」？该列的数据会一起删除。`, '删除').then((ok2) => { if (ok2) colOps.remove(c.id) })
                          }}
                        >
                          删除列
                        </button>
                      </div>
                    )}
                  </>
                )}
              </th>
            ))}
            <th className="col-add" aria-hidden={false}>
              <button
                type="button"
                className="col-add__btn"
                disabled={busy}
                title="在最后新增一列"
                onClick={() => {
                  const last = orderedColumns[orderedColumns.length - 1]
                  if (last === undefined) return
                  colOps.insertRight(last.id)
                }}
              >
                +
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {!group.collapsed && sorted.map((it) => (
            <tr
              key={it.id}
              draggable={!busy}
              onDragStart={(e) => {
                // M9.2-fix5：从单元格内的输入/浮层起拖（选色器、滑块、文本选择）不应触发整行拖拽，
                // 否则松手会误发一次条目重排（用户实测：拖颜色滑块会让整板重排、rev 跳变）。
                if (isFromInnerControl(e.target) || isFromInnerControl(lastPressTarget)) {
                  e.preventDefault()
                  return
                }
                draggingItemId = it.id
                e.dataTransfer.effectAllowed = 'move'
          // 用透明图顶掉浏览器自带拖影，保证只看到我们的浮层卡片
          e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0)
              }}
              onDragEnd={() => { draggingItemId = null }}
              className={
                rowDrop === null || rowDrop.id !== it.id
                  ? undefined
                  : rowDrop.after
                    ? 'row-drop-after'
                    : 'row-drop-before'
              }
              onDragLeave={() => {
                if (draggingItemId !== null) setRowDrop(null)
              }}
              onDragOver={(e) => {
                // 拖的是分组时，行上不显示落点（否则会同时出现两条蓝线）
                if (draggingGroupId !== null) return
                e.preventDefault()
                e.dataTransfer.dropEffect = 'move'
                // 指针在行的下半部分 -> 插到该行之后；上半部分 -> 插到之前
                e.stopPropagation()
                const r = e.currentTarget.getBoundingClientRect()
                dropAfter = (e.clientY - r.top) > r.height / 2
                setRowDrop({ id: it.id, after: dropAfter })
              }}
              onDrop={(e) => {
                if (draggingGroupId !== null) return
                e.preventDefault()
                e.stopPropagation()
                setRowDrop(null)
                onDropItem(it.id, dropAfter)
              }}
            >
              <td className="row-handle-col">
                <span
                  className="row-drag"
                  draggable={!busy}
                  title="拖动调整顺序"
                  onDragStart={(e) => {
                    draggingItemId = it.id
                    e.dataTransfer.effectAllowed = 'move'
          // 用透明图顶掉浏览器自带拖影，保证只看到我们的浮层卡片
          e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0)
                    // 同样让虚影是整行，而不是那个小柄
                    const tr = e.currentTarget.closest('tr')
                    if (tr !== null) e.dataTransfer.setDragImage(tr, 24, tr.clientHeight / 2)
                  }}
                  onDragEnd={() => { draggingItemId = null }}
                >
                  ⋮⋮
                </span>
                {/* 勾选框放在拖动柄之后并留出间距：拖动柄在前，减少误触 */}
                <input
                  type="checkbox"
                  className="row-check"
                  checked={selected.has(it.id)}
                  disabled={busy}
                  aria-label={`Select item: ${it.name}`}
                  onChange={() => onToggleSel(it.id)}
                />
              </td>
              {orderedColumns.map((c) => (
                <td
                key={c.id}
                style={{
                  ...pinStyle(c, pinLeft),
                  textAlign: (columnAlign(c, uiSettings) ?? undefined) as 'left' | 'center' | 'right' | undefined,
                }}
              >
                  <ErrorBoundary>
                    <CellView
                    column={c}
                    item={{ ...it, values: { ...it.values, ...(derived.get(it.id) ?? {}) } }}
                    editing={editing?.itemId === it.id && editing?.columnKey === c.key}
                    busy={busy}
                    onStartEdit={() => onStartEdit(it.id, c.key)}
                    onCommit={(v) => onCommit(it.id, c.key, v)}
                    onCancel={onCancel}
                  />
                  </ErrorBoundary>
                </td>
              ))}
            </tr>
          ))}
          {sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length + 1} className="dim">
                暂无条目
              </td>
            </tr>
          )}
        </tbody>
        <tfoot className="group__footer">
          <tr>
            <td className="row-handle-col" />
            {orderedColumns.map((c) => (
              <td
                key={c.id}
                className="group__footer-cell"
                style={{
                  width: resizeW?.id === c.id ? resizeW.w : c.width,
                  ...pinStyle(c, pinLeft),
                }}
                title={footerAgg.has(c.id)
                  ? footerAgg.get(c.id)!.map((x) => `${x.label}: ${x.n}`).join('\n')
                  : undefined}
              >
                {footerBattery.has(c.id) && (
                  <span className="battery battery--footer" title={`进度平均 ${footerBattery.get(c.id)!.avg.toFixed(1)}%（共 ${footerBattery.get(c.id)!.count} 条有值）`}>
                    <span className="battery__track">
                      <span className="battery__fill" style={{ width: `${footerBattery.get(c.id)!.avg}%` }} />
                    </span>
                    <span className="battery__num">{footerBattery.get(c.id)!.avg.toFixed(0)}%</span>
                  </span>
                )}
                {footerNum.has(c.id) && (() => {
                  const f = footerNum.get(c.id)!
                  // M9.8：组尾统计方式来自数据库（footerAgg），点击循环切换
                  const mode = footerStatMode[c.key] ?? 'avg'
                  const shown =
                    mode === 'sum' ? String(Math.round(f.sum * 100) / 100)
                    : mode === 'count' ? String(f.count)
                    : mode === 'min' ? String(Math.round(f.min * 100) / 100)
                    : mode === 'max' ? String(Math.round(f.max * 100) / 100)
                    : f.avg.toFixed(1)
                  const label =
                    mode === 'sum' ? '合计'
                    : mode === 'count' ? '计数'
                    : mode === 'min' ? '最小'
                    : mode === 'max' ? '最大'
                    : '平均'
                  return (
                    <button
                      type="button"
                      className="group__footer-stat group__footer-stat--click"
                      title={`${label} ${shown}\n计数 ${f.count}｜合计 ${f.sum}｜平均 ${f.avg.toFixed(2)}｜最小 ${f.min}｜最大 ${f.max}\n点击切换`}
                      onClick={() => onCycleAgg(c.key)}
                    >
                      <span className="group__footer-stat__avg">{shown}</span>
                      <span className="group__footer-stat__label">{label}</span>
                    </button>
                  )
                })()}
                {footerBool.has(c.id) && (
                  <div
                    className="group__footer-stat"
                    title={`已勾选 ${footerBool.get(c.id)!.checked} / ${footerBool.get(c.id)!.total}`}
                  >
                    <span className="group__footer-stat__avg">{footerBool.get(c.id)!.checked}</span>
                    <span className="group__footer-stat__label">已勾选</span>
                  </div>
                )}
                {footerAgg.has(c.id) && (
                  <div className="group__footer-agg">
                    <span className="group__footer-agg__bar">
                      {footerAgg.get(c.id)!.map((x) => (
                        <span
                          key={x.label}
                          className="group__footer-agg__seg"
                          style={{ background: x.color, flex: x.n }}
                        />
                      ))}
                    </span>
                    <span className="group__footer-agg__num">{footerTotal.get(c.id)}</span>
                  </div>
                )}
              </td>
            ))}
          </tr>
        </tfoot>
        </table>
      </div>

      <form
        className="group__add"
        onSubmit={(e) => {
          e.preventDefault()
          const name = draft.trim()
          if (name === '' || busy) return
          setDraft('')
          onCreateItem(name)
        }}
      >
        <input
          className="group__add-input"
          placeholder="+ 新增条目名称，回车确认"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
        />
      </form>
        </div>
      </div>    </section>
  )
}

/**
 * 看板视图（P3：单元格可编辑）。
 *
 * 写入走乐观锁：每次 PATCH 带上当前 rev，服务端比对 Board.Rev；
 * 409 冲突时用响应里的 current_rev 提示用户并自动重新载入整板。
 */
// 拖动中的条目 id。HTML5 拖放天然是全局瞬态的，放在模块作用域，
// 免得为它把状态一层层往下传。
let draggingItemId: number | null = null
// 指针落在目标的下半部分时插到它后面（否则插前面）。没有这个就只能往目标之前插，
// 于是永远拖不到列表最末尾。
let dropAfter = false
// 拖动中的列 id（列头左侧的拖动柄会设置它）。null 表示当前不是在拖列。
let draggingColumnId: number | null = null
// 拖动中的分组 id（拖分组色条时设置）。拖动分组与拖入条目是两套语义，
// 放置时按哪个非空来分派。
let draggingGroupId: number | null = null
/**
 * M7：「在右侧插入列」可选的列类型。
 *
 * 只列 BoardCell.SUPPORTED_TYPES 里**已实现编辑**的类型。
 * 之前我列了 12 种，把 people / checkbox / link / email / phone / rating 也摆了出来，
 * 但它们在 isEditable() 判定为只读 —— 建出来不能编辑，等于提供了坏选项。
 * 其余类型等实现后再加回来（spec §5.3 共 28 种）。
 */
/**
 * M9.2-fix5：判断拖拽起点是否来自单元格内部的交互控件。
 * 条目行是 HTML5 draggable 拖动源，若用户在浮层里拖动滑块/选色，会顺带触发整行拖拽，
 * 松手时误发一次 reorder。凡是这些控件里起拖，一律不认。
 */
/**
 * 最近一次 mousedown 的落点。HTML5 拖拽若把 draggable 祖先当成拖动源，
 * dragstart 的 e.target 会是那一行而不是真正的按下元素，所以需要另存一份按下位置来判断。
 */
let lastPressTarget: EventTarget | null = null

function isFromInnerControl(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false
  return (
    target.closest(
      '.cell-palette, .cell-palette__custombox, .labels-panel, .status-panel, .country-list, input, textarea, select'
    ) !== null
  )
}

const COLUMN_TYPE_CHOICES = IMPLEMENTED_COLUMN_TYPES.map((t) => ({ value: t.id, label: t.label }))
/**
 * M8：冻结列的内联样式。
 * 名称列（orderedColumns[0]）与 gutter 已由 CSS 用 nth-child 冻结，这里只处理名称列之后
 * 前 getPinnedCount() 列：left = gutter + 名称列宽 + 前面已冻结列的宽度累加。
 * 宽度取实际渲染宽度（拖动预览时用 resizeW）。
 */
/**
 * M8：冻结列的内联样式。
 *
 * 偏移不能靠常量算 —— CDP 实测发现：gutter 实际 48px（--geo-gutter-w 是 40）、
 * 名称列实际渲染 255px（--geo-name-col-w 是 400，table 自动布局把它压窄了）。
 * 用常量算出来的 left 会把冻结列钉到错误位置（表现为两列重叠、中间留空隙）。
 * 因此改成：挂载后用真实 getBoundingClientRect 量出每列的自然左偏移，存进 pinLeft 表。
 */
function pinStyle(
  col: { id: number },
  pinLeft: Record<number, number>,
): React.CSSProperties {
  const left = pinLeft[col.id]
  if (left === undefined) return {}
  // zIndex 1：低于名称列(2)与 gutter(3)，横向滚动时内容从冻结列下面穿过
  return { position: 'sticky', left, zIndex: 1, background: '#1b2532' }
}
export default function BoardPage() {
  /** 正在拖动的分组（拖动时该组收起成组头，跟手卡片是页面级浮层） */
  const [draggingGroup, setDraggingGroup] = useState<{ id: number; title: string; count: number; color: string; width: number; startY: number; barHeight: number; sectionHeight: number } | null>(null)
  /** 拖动期间指针相对起拖点的纵向位移（源组的 translateY） */
  const [dragDy, setDragDy] = useState(0)
  /** 折叠后单组高度（含组间距），其它组让位用 */
  const [dragStep, setDragStep] = useState(0)

  /**
   * 其它分组的让位位移：源组只能保持 relative + translateY（改成 absolute / display:none
   * 会被 Chrome 取消拖拽），所以它原来的位置还占着；把其它组用 transform 上移/下移一个卡高，
   * 视觉上就等于"空格挪到了落点"。
   */
  const groupShift = (index: number): number => {
    if (draggingGroup === null || groupDrop === null || dragStep === 0 || data === null) return 0
    const ids = data.groups.map((x) => x.id)
    const from = ids.indexOf(draggingGroup.id)
    const target = ids.indexOf(groupDrop.id)
    if (from < 0 || target < 0 || index === from) return 0
    const to = groupDrop.after ? target + 1 : target
    const toIdx = to > from ? to - 1 : to
    if (toIdx === from) return 0
    const reduced = index < from ? index : index - 1
    if (from < toIdx && reduced >= from && reduced <= toIdx - 1) return -dragStep
    if (from > toIdx && reduced >= toIdx && reduced <= from - 1) return dragStep
    return 0
  }
  /** 起拖时的指针 Y */
  const dragStartYRef = useRef(0)
  /** 最后一次 dragover 的指针 Y（松手时用它算插入位置） */
  const lastPointerYRef = useRef(0)
  /** 起拖时的页面滚动位置（源组跟手位移要补偿滚动） */
  const dragStartScrollRef = useRef(0)
  /**
   * 拖动期间给页面补的底部留白（px）。
   *
   * 折叠被拖组会让文档变矮；若此时已滚到接近底部，浏览器会把 scrollY 夹回去，
   * 整页内容向上跳 → 拖动源在屏幕上移位 → Chrome 立刻取消拖拽。
   * 补上等量留白后文档高度不变（底部组拖不动的直接原因）。
   */
  const [dragPad, setDragPad] = useState(0)
  /**
   * 源组上方各组折叠后少掉的总高度：
   * ① 加进源组 translateY 把它推回原位；② 折叠后把视口上滚同样的距离，避免停在留白区。
   */
  const [dragLift, setDragLift] = useState(0)
  /** 起拖前的文档高度，用来算需要补多少留白 */
  const preDragDocHeightRef = useRef(0)
  /**
   * 折叠后把视口上滚同样的距离。内容折叠了 lift 那么多，不滚就会停在底部留白里（整页空白）。
   * 放在 useLayoutEffect 里，赶在浏览器夹住 scrollY 之前滚到位。
   */
  useLayoutEffect(() => {
    if (draggingGroup === null || dragLift <= 0) return
    const target = Math.max(0, dragStartScrollRef.current - dragLift)
    if (Math.abs(window.scrollY - target) > 1) window.scrollTo(0, target)
  }, [draggingGroup, dragLift])

  /** 拖拽结束兜底：源节点若因为任何原因没收到 dragend，这里强制复位 */
  useEffect(() => {
    if (draggingGroup === null) return
    const clear = () => {
      draggingGroupId = null
      setDraggingGroup(null)
      setDragDy(0)
      setDragPad(0)
      setDragLift(0)
      setGroupDrop(null)
    }
    window.addEventListener('dragend', clear)
    window.addEventListener('drop', clear)
    return () => {
      window.removeEventListener('dragend', clear)
      window.removeEventListener('drop', clear)
    }
  }, [draggingGroup])

  /** 分组拖动落点（页面统一持有，供插入线与标题栏共用） */
  const [groupDrop, setGroupDrop] = useState<{ id: number; after: boolean } | null>(null)

  const { id, itemId } = useParams<{ id: string; itemId?: string }>()
  const navigate = useNavigate()
  // M9.3-fix2：按住 Ctrl/⌘ 时给 <body> 打标记，让链接/邮箱单元格的指针变成"手型"
  useEffect(() => {
    const sync = (e: KeyboardEvent) => {
      const on = e.ctrlKey || e.metaKey
      document.body.dataset.modkey = on ? '1' : '0'
    }
    const clear = () => { document.body.dataset.modkey = '0' }
    window.addEventListener('keydown', sync)
    window.addEventListener('keyup', sync)
    window.addEventListener('blur', clear)
    return () => {
      window.removeEventListener('keydown', sync)
      window.removeEventListener('keyup', sync)
      window.removeEventListener('blur', clear)
    }
  }, [])
  // M9.2-fix7：记录最近一次按下位置，供行级拖拽守卫判断"是不是从浮层/输入框里起拖"
  useEffect(() => {
    const onDown = (ev: MouseEvent) => { lastPressTarget = ev.target }
    document.addEventListener('mousedown', onDown, true)
    return () => document.removeEventListener('mousedown', onDown, true)
  }, [])

  const [data, setData] = useState<BoardResponse | null>(null)
  /**
   * 拖动期间的指针跟踪（P0）。
   *
   * 关键：**不再依赖"哪个元素接到 dragover"**。源组被 translateY 抬起、又在上层，
   * 指针下面永远是它自己 —— 之前因此只有在页面滚动后才会偶然触发一次让位。
   * 现在统一在 window 上按指针 Y + 各组矩形几何计算：
   *   · dragDy：源组跟手位移（含页面滚动补偿）
   *   · groupDrop：落点（插到某组之前/之后），供插入线与让位使用
   */
  useEffect(() => {
    if (draggingGroup === null || data === null) return

    const others = () =>
      data.groups
        .filter((x) => x.id !== draggingGroup.id)
        .map((x) => {
          const el = document.querySelector<HTMLElement>(`[data-group-id="${x.id}"]`)
          const r = el === null ? null : el.getBoundingClientRect()
          return r === null ? null : { id: x.id, top: r.top, bottom: r.bottom }
        })
        .filter((v): v is { id: number; top: number; bottom: number } => v !== null)
        .sort((a, b) => a.top - b.top)

    const syncDy = () => {
      setDragDy(
        lastPointerYRef.current - dragStartYRef.current + (window.scrollY - dragStartScrollRef.current),
      )
    }

    const computeDrop = (pointerY: number) => {
      const list = others()
      if (list.length === 0) return
      // 落在某个组的上半 → 插它前面；下半 → 插它后面；超出所有组 → 末尾
      let next: { id: number; after: boolean } | null = null
      for (const g of list) {
        if (pointerY < g.top) { next = { id: g.id, after: false }; break }
        if (pointerY <= g.bottom) { next = { id: g.id, after: pointerY > (g.top + g.bottom) / 2 }; break }
      }
      if (next === null) {
        const last = list[list.length - 1]
        next = last === undefined ? null : { id: last.id, after: true }
      }
      setGroupDrop(next)
    }

    const onOver = (ev: DragEvent) => {
      lastPointerYRef.current = ev.clientY
      syncDy()
      computeDrop(ev.clientY)
    }
    const onScroll = () => syncDy()

    window.addEventListener('dragover', onOver)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('dragover', onOver)
      window.removeEventListener('scroll', onScroll)
    }
  }, [draggingGroup, data])

  /**
   * 拖拽自动滚动（P1）：指针进入视口上下边缘 80px 时滚动页面。
   * 没有这个，视口外的分组永远"够不到"（最下面的组拖不动的直接原因）。
   */
  useEffect(() => {
    if (draggingGroup === null) return
    let raf = 0
    const tick = () => {
      const y = lastPointerYRef.current
      const h = window.innerHeight
      const edge = 80
      let dy = 0
      if (y < edge) dy = -Math.max(4, Math.ceil((edge - y) / 5))
      else if (y > h - edge) dy = Math.max(4, Math.ceil((y - (h - edge)) / 5))
      if (dy !== 0) {
        window.scrollBy(0, dy)
        setDragDy(
          lastPointerYRef.current - dragStartYRef.current + (window.scrollY - dragStartScrollRef.current),
        )
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [draggingGroup])

  // M6：URL 里带 itemId 就滑出详情面板（同一个看板组件，面板浮在上面）
  const detailId = itemId === undefined ? null : Number(itemId)
  const allItems = data === null ? [] : [...data.items].sort((a, b) => a.pos - b.pos)
  const detailIdx = detailId === null ? -1 : allItems.findIndex((x) => x.id === detailId)
  const detailItem = detailIdx < 0 ? null : (allItems[detailIdx] ?? null)

  // M6：面板打开时按 Esc 关闭（只在面板存在时挂监听，避免影响看板其它键盘操作）
  useEffect(() => {
    if (detailItem === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') navigate(`/boards/${id}`)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [detailItem, id, navigate])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<Editing>(null)
  // M6：详情面板用自己的编辑状态。若与看板共用 editing，同一单元格会在两处同时进入编辑态：
  // 看板那侧的编辑器抢焦点（输入无效），且每次点击都会触发一次 commit（每点一次一个 PATCH）。
  const [detailEditing, setDetailEditing] = useState<string | null>(null)

  // M8：把看板显示设置（ViewState 的 ItemHeight / HeaderHeight）同步到 CSS 变量
  useEffect(() => {
    if (data === null) return
    let vs: Record<string, unknown> = {}
    try {
      const raw = data.board.viewState
      const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
      if (parsed !== null && typeof parsed === 'object') vs = parsed as Record<string, unknown>
    } catch {
      vs = {}
    }
    const ih = typeof vs.ItemHeight === 'number' ? vs.ItemHeight : 0
    const hh = typeof vs.HeaderHeight === 'number' ? vs.HeaderHeight : 0
    const pc = typeof vs.PinnedColumns === 'number' ? vs.PinnedColumns : 0
    setPinnedCount(pc)
    applyRowAndHeaderHeight(ih, hh)
  }, [data])
  // M7：正在编辑标签的列 id
  const [labelsFor, setLabelsFor] = useState<number | null>(null)
  // M8：显示设置面板
  const [settingsOpen, setSettingsOpen] = useState(false)
  /** M9.6：正在编辑公式的列 id */
  const [formulaFor, setFormulaFor] = useState<number | null>(null)
  /** M9.8：正在设置权重/开关的进度列 id */
  const [numFormatFor, setNumFormatFor] = useState<number | null>(null)
  const [progressFor, setProgressFor] = useState<number | null>(null)
  // M8：冻结列数（用 state 而非模块变量 —— 模块变量变了不会触发重渲染，
  /** M9.8：组尾统计方式的本地覆盖（真源在数据库 footerAgg） */
  const [footerAggLocal, setFooterAggLocal] = useState<Record<string, string>>({})

  /** 数据库里的组尾统计方式 + 本次点击的乐观覆盖 */
  const footerAgg: Record<string, string> = {
    ...(data === null ? {} : (data.footerAgg ?? {})),
    ...footerAggLocal,
  }

  /** M10：改单列对齐（写列 settings.align；空串 = 跟随全局） */
  /**
   * M11-1：批量结构操作的执行器。
   * 服务端每个结构接口都带 If-Match 乐观锁，所以必须**串行**执行并逐次更新 rev。
   */
  const runBatchStructure = async (
    label: string,
    ids: number[],
    fn: (rev: number, id: number) => Promise<{ rev: number }>,
  ) => {
    if (data === null || ids.length === 0) return
    setBusy(true)
    setNotice('')
    let rev = data.board.rev
    let done = 0
    try {
      for (const id of ids) {
        const res = await fn(rev, id)
        rev = res.rev
        done += 1
      }
      await load()
      setNoticeKind('ok')
      setNotice(`${label}：${done} 列（Rev ${rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(`${label}失败（已完成 ${done} 列）：${e instanceof Error ? e.message : String(e)}`)
      try {
        await load()
      } catch {
        /* 重载失败就保持现状 */
      }
    } finally {
      setBusy(false)
    }
  }

  const batchDeleteColumns = (ids: number[]) =>
    void runBatchStructure('已删除列', ids, (rev, id) => deleteColumn(data!.board.id, rev, id))

  const batchChangeColumnType = (ids: number[], type: string) =>
    void runBatchStructure('已改变列类型', ids, (rev, id) => convertColumnType(data!.board.id, rev, id, type, false))

  const batchAlignColumns = (ids: number[], align: string) => {
    // 锁定列（formula/boolean/tag/timerange/rating/color/dropdown/columns-battery）不吃对齐设置，直接跳过
    const targets = ids.filter((id) => {
      const col = data?.columns.find((c) => c.id === id)
      return col !== undefined && ALIGN_LOCKED[col.type] === undefined
    })
    if (targets.length === 0) {
      setNoticeKind('warn')
      setNotice('选中的列都不支持对齐（公式/复选框/标签/时间线/评分/色块）')
      return
    }
    void runBatchStructure('已设置对齐', targets, (rev, id) => {
      const col = data!.columns.find((c) => c.id === id)
      if (col === undefined) return Promise.resolve({ rev })
      const next = { ...readColumnSettings(col) }
      if (align === '') delete next.align
      else next.align = align
      return updateColumn(data!.board.id, rev, id, { settings: JSON.stringify(next) })
    })
  }

  const batchResizeColumns = (ids: number[], width: number) =>
    void runBatchStructure('已调整列宽', ids, (rev, id) => updateColumn(data!.board.id, rev, id, { width }))

  const changeColumnAlign = (columnId: number, align: string) => {
    void (async () => {
      if (data === null) return
      const col = data.columns.find((x) => x.id === columnId)
      if (col === undefined) return
      // 点的是当前正在生效的值 → 什么都不做（不发请求、不产生提示）
      if (String(readColumnSettings(col).align ?? '') === align) return
      const next = { ...readColumnSettings(col) }
      if (align === '') delete next.align
      else next.align = align
      try {
        await updateColumn(data.board.id, data.board.rev, columnId, { settings: JSON.stringify(next) })
        await load()
        setNoticeKind('ok')
        setNotice(align === '' ? '该列已改为跟随全局对齐' : `该列对齐：${align}`)
      } catch (e) {
        setNoticeKind('warn')
        setNotice(`设置对齐失败：${e instanceof Error ? e.message : String(e)}`)
      }
    })()
  }

  /** M10：改看板级默认对齐（存 AppSetting 的 ui.columnAlign） */
  /** backlog #5：组间隙（AppSetting ui.groupGap，默认 16） */
  const groupGap = (() => {
    const raw = data?.uiSettings?.groupGap
    const n = raw === undefined ? Number.NaN : Number(raw)
    return Number.isFinite(n) ? Math.max(0, Math.min(64, Math.round(n))) : 16
  })()

  const changeGlobalAlign = (align: string) => {
    void (async () => {
      if (data === null) return
      if ((data.uiSettings?.columnAlign ?? 'left') === align) return
      try {
        await setBoardAppSetting(data.board.id, 'columnAlign', align)
        await load()
        setNoticeKind('ok')
        setNotice(`看板默认对齐：${align}`)
      } catch (e) {
        setNoticeKind('warn')
        setNotice(`设置默认对齐失败：${e instanceof Error ? e.message : String(e)}`)
      }
    })()
  }

  /** 点击组尾统计值：avg -> sum -> count -> min -> max -> avg，并写进数据库 */
  const cycleFooterAgg = (columnKey: string) => {
    const order: readonly string[] = ['avg', 'sum', 'count', 'min', 'max']
    const cur: string = footerAgg[columnKey] ?? 'avg'
    const next = order[(order.indexOf(cur) + 1) % order.length] ?? 'avg'
    setFooterAggLocal((prev) => ({ ...prev, [columnKey]: next }))
    void (async () => {
      try {
        if (data !== null) await setFooterAgg(data.board.id, columnKey, next)
      } catch (e) {
        setFooterAggLocal((prev) => ({ ...prev, [columnKey]: cur }))
        setNoticeKind('warn')
        setNotice(`保存组尾统计方式失败：${e instanceof Error ? e.message : String(e)}`)
      }
    })()
  }
  // 会导致首帧不冻结、随后因其它状态变化突然冻结而错位）
  const [pinnedCount, setPinnedCount] = useState(0)
  // 已归档看板只读：所有写操作按钮统一置灰（服务端另有校验待补）
  const readOnly = data?.board.archived === true
  // M8：看板 ⋮ 菜单（fixed 定位，坐标取触发键实测值）
  const [boardMenu, setBoardMenu] = useState<{ x: number; y: number } | null>(null)
  /** 鼠标在看板菜单里移动：离开二级触发项/浮窗就收起二级 */
  const onBoardMenuHover = (e: { target: EventTarget | null }) => {
    const t = e.target as HTMLElement | null
    if (t === null) return
    if (t.closest('.col-menu--sub') !== null) return   // 在浮窗内
    if (t.closest('[data-sub="1"]') !== null) return   // 在二级触发项上
    setBoardSub(null)
  }

  // 看板菜单关掉时，二级面板状态一并清空（否则下次打开还留着）
  useEffect(() => {
    if (boardMenu === null) setBoardSub(null)
  }, [boardMenu])

  /** 打开二级浮窗：坐标取"父菜单右边缘"和"触发项上边缘"，避免压住父菜单/上下错位 */
  const openBoardSub = (kind: 'import' | 'export') => (e: { currentTarget: HTMLElement }) => {
    const r = e.currentTarget.getBoundingClientRect()
    const menu = e.currentTarget.closest('.board-menu')
    const mr = menu === null ? null : menu.getBoundingClientRect()
    setBoardSub({ kind, x: mr === null ? r.right - 2 : mr.right - 2, y: r.top - 6 })
  }

  /** M10：看板菜单的二级面板（导入 / 导出） */
  const [colCenterOpen, setColCenterOpen] = useState(false)
  const [boardSub, setBoardSub] = useState<{ kind: 'import' | 'export'; x: number; y: number } | null>(null)
  /** M10：导入 JSON 用隐藏的文件选择框 */
  const importRef = useRef<HTMLInputElement>(null)
  /** M10-4：导入 monday.com XLSX 用 */
  const xlsxRef = useRef<HTMLInputElement>(null)
  // M8：归档视图
  const [archiveOpen, setArchiveOpen] = useState(false)

  // 把「打开标签编辑」注册给状态/下拉浮层（它们拿不到 props，用注册表通信）
  useEffect(() => {
    setEditLabelsHandler((columnId: number) => setLabelsFor(columnId))
    return () => setEditLabelsHandler(null)
  }, [])

  // M9.3 遗留 #2：看板级标签池 —— 扫描 tag 列的历史值去重，供编辑器做候选
  useEffect(() => {
    if (data === null) {
      setTagPool({})
      return
    }
    const pool: Record<string, string[]> = {}
    for (const c of data.columns) {
      if (c.type !== 'tag') continue
      const counts = new Map<string, number>()
      for (const it of data.items) {
        const raw = (it.values[c.key] ?? '').trim()
        if (raw === '') continue
        let list: string[]
        try {
          const parsed: unknown = JSON.parse(raw)
          list = Array.isArray(parsed) ? parsed.map((x) => String(x)) : [raw]
        } catch {
          list = [raw]
        }
        for (const t of list) {
          const v = t.trim()
          if (v === '') continue
          counts.set(v, (counts.get(v) ?? 0) + 1)
        }
      }
      // 用得多得排前面，便于点选
      pool[c.key] = [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([t]) => t)
    }
    setTagPool(pool)
  }, [data])
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState('')
  const [noticeKind, setNoticeKind] = useState<'ok' | 'warn'>('ok')

  const load = useCallback(async () => {
    if (id === undefined) return
    const d = await getBoard(id)
    setData(d)
  }, [id])

  useEffect(() => {
    if (id === undefined) return
    let alive = true
    setLoading(true)
    setError('')
    setData(null)
    setEditing(null)
    setNotice('')

    getBoard(id)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })

    return () => {
      alive = false
    }
  }, [id])

  const commit = async (itemId: number, columnKey: string, value: string) => {
    if (data === null) return

    const prevRev = data.board.rev
    setBusy(true)
    setNotice('')

    try {
      const res = await patchCell(data.board.id, itemId, columnKey, value, prevRev)
      setData((d) => (d === null ? d : applyCell(d, res)))
      setNoticeKind('ok')
      setNotice(`已保存（Rev ${res.rev}）`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        const cur = (e.payload as { current_rev?: number } | undefined)?.current_rev
        setNoticeKind('warn')
        setNotice(
          `与其它改动冲突${cur === undefined ? '' : `（服务端 Rev 已是 ${cur}）`}，已重新载入看板`,
        )
        try {
          await load()
        } catch {
          // 重新载入也失败就保持当前画面，下面统一给提示
        }
      } else if (e instanceof ApiError) {
        setNoticeKind('warn')
        setNotice(`保存失败：${e.message}`)
      } else {
        setNoticeKind('warn')
        setNotice(`保存失败：${e instanceof Error ? e.message : String(e)}`)
      }
    } finally {
      setBusy(false)
      setEditing(null)
    }
  }

  // ---- 拖动排序（条目；组内与跨组） ----
  // 接口约定是「给出目标分组的新完整顺序」，所以这里先在本地算出顺序再提交，
  // 服务端负责重编号 + 乐观锁。
  const dropOnItem = (targetItemId: number, after: boolean) => {
    const from = draggingItemId
    if (from === null || data === null) return

    const target = data.items.find((it) => it.id === targetItemId)
    if (target === undefined) return

    const list = data.items
      .filter((it) => it.groupId === target.groupId)
      .sort((a, b) => a.pos - b.pos)
      .map((it) => it.id)

    const fromIdx = list.indexOf(from)
    let toIdx = list.indexOf(target.id)
    if (toIdx < 0) return
    if (fromIdx >= 0) {
      list.splice(fromIdx, 1)
      if (fromIdx < toIdx) toIdx -= 1
    }
    list.splice(after ? toIdx + 1 : toIdx, 0, from)

    void applyOrder('item', target.groupId, list)
  }

  const dropToGroup = (groupId: number) => {
    const from = draggingItemId
    if (from === null || data === null) return

    const list = data.items
      .filter((it) => it.groupId === groupId && it.id !== from)
      .sort((a, b) => a.pos - b.pos)
      .map((it) => it.id)
    list.push(from)

    void applyOrder('item', groupId, list)
  }

  const dropOnColumn = (targetColumnId: number, after: boolean) => {
    const moving = draggingColumnId
    if (moving === null || data === null) return

    const list = data.columns.map((c) => c.id)
    const fromIdx = list.indexOf(moving)
    let toIdx = list.indexOf(targetColumnId)
    if (fromIdx < 0 || toIdx < 0) return

    list.splice(fromIdx, 1)
    if (fromIdx < toIdx) toIdx -= 1
    list.splice(after ? toIdx + 1 : toIdx, 0, moving)

    void applyOrder('column', 0, list)
  }
  // ---- 结构增删（P5）：成功或 409 都整板重载，rev 与顺序以服务端为准 ----
  /** M8：归档单个条目 */
  const doArchiveItem = async (itemId: number) => {
    if (data === null) return
    const target = data.items.find((x) => x.id === itemId)
    const okA = await askConfirm(
      '归档条目？',
      `「${target?.name ?? itemId}」将从看板隐藏，可在看板 ⋮ →「查看归档」里恢复。`,
      '归档',
    )
    if (!okA) return
    await runStructure('已归档条目', () => archiveItem(data.board.id, data.board.rev, itemId, true))
  }
  const doCreateItem = async (groupId: number, name: string) => {
    if (data === null) return
    setBusy(true)
    setNotice('')
    try {
      const res = await createItem(data.board.id, groupId, name, data.board.rev)
      // M8：应用「默认条目取值」—— 逐列写入，rev 逐次链式推进
      let rev = res.rev
      const defs = readItemDefaults(data.board.viewState)
      for (const [k, v] of Object.entries(defs)) {
        if (v === '') continue
        const pr = await patchCell(data.board.id, res.itemId, k, v, rev)
        rev = pr.rev
      }
      await load()
      setNoticeKind('ok')
      setNotice(`已新增条目（Rev ${res.rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(
        e instanceof ApiError && e.status === 409
          ? '与其它改动冲突，已重新载入看板'
          : `新增失败：${e instanceof Error ? e.message : String(e)}`,
      )
      if (e instanceof ApiError && e.status === 409) {
        try {
          await load()
        } catch {
          /* 重载失败就保持现状 */
        }
      }
    } finally {
      setBusy(false)
    }
  }

  const doDeleteItem = async (itemId: number) => {
    if (data === null) return
    setBusy(true)
    setNotice('')
    try {
      const res = await deleteItem(data.board.id, itemId, data.board.rev)
      await load()
      setNoticeKind('ok')
      setNotice(`已删除条目（Rev ${res.rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(
        e instanceof ApiError && e.status === 409
          ? '与其它改动冲突，已重新载入看板'
          : `删除失败：${e instanceof Error ? e.message : String(e)}`,
      )
      if (e instanceof ApiError && e.status === 409) {
        try {
          await load()
        } catch {
          /* 重载失败就保持现状 */
        }
      }
    } finally {
      setBusy(false)
    }
  }

  /**
   * 落点计算：按指针 Y 在所有分组（不含被拖那组）里找插入位置。
   *
   * 为什么不看"哪个元素接了 dragover"：被拖组折叠/位移时指针经常落在它自己身上，
   * 于是算出"没变化"。改成几何判定后，落在卡片中间也能稳定算出一个位置。
   */
  const dropGroupAtPointer = () => {
    const moving = draggingGroupId
    if (moving === null || data === null) return
    const y = lastPointerYRef.current

    // 其它分组的矩形（排除被拖那组），按屏幕位置排序
    const others = data.groups
      .filter((g) => g.id !== moving)
      .map((g) => {
        const el = document.querySelector<HTMLElement>(`[data-group-id="${g.id}"]`)
        const r = el === null ? null : el.getBoundingClientRect()
        return { id: g.id, mid: r === null ? 0 : r.top + r.height / 2, top: r === null ? 0 : r.top }
      })
      .sort((a, b) => a.top - b.top)
    if (others.length === 0) return

    let insertAt = others.length
    for (let i = 0; i < others.length; i += 1) {
      if (y < others[i]!.mid) { insertAt = i; break }
    }

    const list = others.map((x) => x.id)
    list.splice(insertAt, 0, moving)

    const current = data.groups.map((x) => x.id)
    if (list.every((id, i) => id === current[i])) {
      setNoticeKind('warn')
      setNotice('位置没有变化')
      return
    }

    void applyOrder('group', 0, list)
  }

  // ---- M2：结构增删改的通用提交：成功后整板重载，409 也重载 ----
  const runStructure = async (label: string, fn: () => Promise<{ rev: number }>) => {
    if (data === null) return
    setBusy(true)
    setNotice('')
    try {
      const res = await fn()
      await load()
      setNoticeKind('ok')
      setNotice(`${label}（Rev ${res.rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(
        e instanceof ApiError && e.status === 409
          ? '与其它改动冲突，已重新载入看板'
          : `${label}失败：${e instanceof Error ? e.message : String(e)}`,
      )
      if (e instanceof ApiError && e.status === 409) {
        try {
          await load()
        } catch {
          /* 重载失败就保持现状 */
        }
      }
    } finally {
      setBusy(false)
    }
  }

  // M4：行勾选（条目 id 集合）。注意必须放在 BoardPage 里 —— GroupBlock 里也有 menuFor，
  // 之前把状态插进了 GroupBlock，导致勾选框读不到。
  const [selected, setSelected] = useState<Set<number>>(() => new Set())

  // ---- M4：勾选与批量操作 ----
  const toggleSel = (itemId: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  /** 组级全选/取消：monday 的表头全选语义是按分组 */
  const toggleGroupSel = (itemIds: number[]) => {
    setSelected((prev) => {
      const allIn = itemIds.every((id) => prev.has(id))
      const next = new Set(prev)
      for (const id of itemIds) {
        if (allIn) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }
  /** 导出指定分组的条目为 CSV */
  const exportGroupCsv = (groupId: number) => {
    if (data === null) return
    const cols = [...data.columns].sort(
      (a, b) => (a.key === 'name' ? 0 : 1) - (b.key === 'name' ? 0 : 1) || a.pos - b.pos,
    )
    const rows: string[][] = []
    for (const it of data.items) {
      if (it.groupId !== groupId) continue
      rows.push(cols.map((c) => (c.key === 'name' ? it.name : (it.values[c.key] ?? ''))))
    }
    if (rows.length === 0) {
      setNoticeKind('warn')
      setNotice('该分组没有条目可导出')
      return
    }
    const esc = (v: string) => '"' + v.replace(/"/g, '""') + '"'
    const csv = [cols.map((c) => c.title), ...rows].map((r) => r.map(esc).join(',')).join('\r\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'friday-group-' + new Date().toISOString().slice(0, 10) + '.csv'
    a.click()
    URL.revokeObjectURL(url)
    setNoticeKind('ok')
    setNotice(`已导出 ${rows.length} 条`)
  }

  /**
   * M7：改变列类型（两步）。
   * 1) 选目标类型 → 2) 调 dryRun 只统计 → 3) 弹确认报出「转换 N 条 / 清空 M 条」→ 4) 正式转换。
   * 规则见 PLAN.md §3l；不静默丢数据。
   */
  const changeColumnType = async (columnId: number, current: string) => {
    if (data === null) return
      const nameOf = (t: string) => columnTypeLabel(t)
    const pick = await askSelect(
      '改变列类型',
      `当前类型：${nameOf(current)}。改为（仅列出已实现的类型）：`,
      COLUMN_TYPE_CHOICES.map((t) => ({ value: t.value, label: t.label })),
      '下一步',
    )
    if (pick === null || pick === current) return

    let preview: { converted: number; cleared: number }
    try {
      preview = await convertColumnType(data.board.id, data.board.rev, columnId, pick, true)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(`无法转换：${e instanceof Error ? e.message : String(e)}`)
      return
    }

    const msg =
      preview.cleared > 0
        ? `将转换 ${preview.converted} 条，其中 ${preview.cleared} 条数据无法转换、会被清空。此操作不可撤销。`
        : `将转换 ${preview.converted} 条数据，全部可转换。此操作不可撤销。`
    if (!(await askConfirm(`改为「${nameOf(pick)}」？`, msg, '改变'))) return

    await runStructure('已改变列类型', () =>
      convertColumnType(data!.board.id, data!.board.rev, columnId, pick, false),
    )
  }
  /** M7：在指定列右侧插入一个新列（选类型 → 建列，key 自动生成） */
  const insertColumnRight = async (columnId: number) => {
    if (data === null) return
    const pick = await askSelect('在右侧插入列', '选择新列的类型（其余类型编辑器尚未实现，故未列出）：', COLUMN_TYPE_CHOICES.map((t) => ({ value: t.value, label: t.label })), '创建')
    if (pick === null) return
    const label = COLUMN_TYPE_CHOICES.find((t) => t.value === pick)?.label ?? pick
    const key = 'col_' + Date.now().toString(36)
    await runStructure('已插入列', () =>
      createColumn(data.board.id, data.board.rev, {
        key,
        title: label,
        type: pick,
        width: 160,
        afterColumnId: columnId,
      }),
    )
  }
  /** 复制列：只能复制 key/title/type/width，列配置（labels/settings）暂无接口 */
  const duplicateColumn = (columnId: number) => {
    if (data === null) return
    const col = data.columns.find((c) => c.id === columnId)
    if (col === undefined) return
    const key = col.key + '_copy' + Date.now().toString().slice(-6)
    void runStructure('已复制列', () =>
      createColumn(data.board.id, data.board.rev, {
        key,
        title: col.title + '（副本）',
        type: col.type,
        width: col.width,
        // 复制列的配置：状态/下拉的标签与颜色都在这里，不带过去就成空白列
        labels: JSON.stringify(col.labels ?? {}),
        settings: JSON.stringify(col.settings ?? {}),
      }),
    )
  }
  /** 导出选中条目为 CSV（纯前端生成，不依赖后端） */
  const bulkExport = () => {
    if (data === null || selected.size === 0) return
    const cols = [...data.columns].sort(
      (a, b) => (a.key === 'name' ? 0 : 1) - (b.key === 'name' ? 0 : 1) || a.pos - b.pos,
    )
    const head = cols.map((c) => c.title)
    const rows: string[][] = []
    for (const g of data.groups) {
      for (const it of data.items) {
        if (it.groupId !== g.id || !selected.has(it.id)) continue
        const line = cols.map((c) => (c.key === 'name' ? it.name : (it.values[c.key] ?? '')))
        rows.push(line)
      }
    }
    const esc = (s: string) => '"' + s.replace(/"/g, '""') + '"'
    const csv = [head, ...rows].map((r) => r.map(esc).join(',')).join('\r\n')
    // 带 BOM，Excel 打开中文不乱码
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'friday-board-' + new Date().toISOString().slice(0, 10) + '.csv'
    a.click()
    URL.revokeObjectURL(url)
    setNoticeKind('ok')
    setNotice(`已导出 ${rows.length} 条`)
  }

  /** 批量删除：逐个调用删除接口，最后一次性重载 */
  const bulkDelete = async () => {
    if (data === null || selected.size === 0) return
    if (!(await askConfirm('删除选中条目？', `将删除选中的 ${selected.size} 个条目，此操作不可撤销。`, '删除'))) return
    setBusy(true)
    let ok = 0
    let rev = data.board.rev
    try {
      for (const id of selected) {
        try {
          const res = await deleteItem(data.board.id, id, rev)
          rev = res.rev
          ok += 1
        } catch {
          // 单条失败不中断其余
        }
      }
      setSelected(new Set())
      await load()
      setNoticeKind('ok')
      setNotice(`已删除 ${ok} 个条目（Rev ${rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(`批量删除失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }

  /** 批量移动：选目标分组后逐个移动 */
  const bulkMove = async () => {
    if (data === null || selected.size === 0) return
    const opts = data.groups.map((g) => ({ value: String(g.id), label: g.title }))
    const pick = await askSelect('移动到分组', `将选中的 ${selected.size} 个条目移动到：`, opts, '移动')
    if (pick === null) return
    const toGroupId = Number(pick)
    setBusy(true)
    let done = 0
    let rev = data.board.rev
    try {
      for (const id of selected) {
        try {
          const res = await moveItem(data.board.id, rev, id, toGroupId)
          rev = res.rev
          done += 1
        } catch {
          // 单条失败不中断
        }
      }
      setSelected(new Set())
      await load()
      setNoticeKind('ok')
      setNotice(`已移动 ${done} 个条目（Rev ${rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(`批量移动失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }
  /** 批量复制：调用带值复制接口（接口自己会加「（副本）」） */
  const bulkDuplicate = async () => {
    if (data === null || selected.size === 0) return
    if (!(await askConfirm('复制选中条目？', `将复制选中的 ${selected.size} 个条目（含单元格值）。`, '复制'))) return
    setBusy(true)
    let done = 0
    let rev = data.board.rev
    try {
      for (const id of selected) {
        try {
          const res = await duplicateItem(data.board.id, rev, id)
          rev = res.rev
          done += 1
        } catch {
          // 单条失败不中断其余
        }
      }
      setSelected(new Set())
      await load()
      setNoticeKind('ok')
      setNotice(`已复制 ${done} 个条目（含单元格值，Rev ${rev}）`)
    } catch (e) {
      setNoticeKind('warn')
      setNotice(`批量复制失败：${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setBusy(false)
    }
  }
  const applyOrder = async (kind: 'item' | 'column' | 'group', groupId: number, order: number[]) => {
    if (data === null) return

    setBusy(true)
    setNotice('')
    try {
      const res = await reorder(data.board.id, kind, groupId, order, data.board.rev)
      await load() // 顺序以服务端为准，直接重载
      setNoticeKind('ok')
      setNotice(`已重排（Rev ${res.rev}）`)
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setNoticeKind('warn')
        setNotice('与其它改动冲突，已重新载入看板')
        try {
          await load()
        } catch {
          /* 重载失败就保持现状 */
        }
      } else {
        setNoticeKind('warn')
        setNotice(`重排失败：${e instanceof Error ? e.message : String(e)}`)
      }
    } finally {
      setBusy(false)
    }
  }
  return (
    <div
          className="page page--wide"
        style={{ ['--group-gap']: `${groupGap}px`, ...(dragPad > 0 ? { paddingBottom: dragPad } : {}) } as React.CSSProperties}
        >
      {/* M10：隐藏的文件选择框（放页面根，不随菜单卸载）。JSON=我们的导出；XLSX=monday.com 导出 */}
      <input
        ref={importRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null
          e.target.value = ''
          if (f === null) return
          setBoardMenu(null)
          setBoardSub(null)
          void (async () => {
            try {
                const parsed = JSON.parse(await f.text()) as unknown
                const res = await importIntoBoard(data!.board.id, parsed)
                await load()
                setNoticeKind('ok')
                setNotice(`已导入：分组 +${res.groupsCreated ?? 0}/复用 ${res.groupsReused ?? 0}，列 +${res.columnsCreated ?? 0}/复用 ${res.columnsReused ?? 0}，条目 ${res.itemsCreated ?? 0}`)
            } catch (err) {
              setNoticeKind('warn')
              setNotice(`导入失败：${err instanceof Error ? err.message : String(err)}`)
            }
          })()
        }}
      />
      <input
        ref={xlsxRef}
        type="file"
        accept=".xlsx"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0] ?? null
          e.target.value = ''
          if (f === null) return
          setBoardMenu(null)
          setBoardSub(null)
          void (async () => {
            try {
              const parsed = await parseMondayXlsx(f)
              const res = await importIntoBoard(data!.board.id, parsed.payload)
              await load()
              setNoticeKind('ok')
              setNotice(`monday 导入完成：分组 +${res.groupsCreated ?? 0}/复用 ${res.groupsReused ?? 0}，列 +${res.columnsCreated ?? 0}/复用 ${res.columnsReused ?? 0}，条目 ${res.itemsCreated ?? 0}`)
            } catch (err) {
              setNoticeKind('warn')
              setNotice(`monday 导入失败：${err instanceof Error ? err.message : String(err)}`)
            }
          })()
        }}
      />
      <div className="page__head">
        <h1 className="page__title">{data?.board.name ?? '看板'}</h1>
        {data !== null && <span className="badge">Rev {data.board.rev}</span>}
        {busy && <span className="badge badge--todo">保存中…</span>}
        {data !== null && (
          <button
            type="button"
            className="board-menu-btn"
            aria-label="看板菜单"
            title="看板菜单"
            onClick={(e) => {
              if (boardMenu !== null) {
                setBoardMenu(null)
                return
              }
              const r = e.currentTarget.getBoundingClientRect()
              setBoardMenu({ x: r.right - 240, y: r.bottom + 6 })
            }}
          >
            ⋮
          </button>
        )}
        <Link className="page__link" to="/">
          返回
        </Link>
      </div>
      {data !== null && data.board.archived && (
        <div className="board-archived-banner">
          <span>该看板已归档（编辑已禁用）</span>
          <button
            type="button"
            className="board-archived-banner__btn"
            disabled={busy}
            onClick={() => {
              void (async () => {
                try {
                  await archiveBoard(data.board.id, data.board.rev, false)
                  await load()
                  setNoticeKind('ok')
                  setNotice('已取消归档')
                } catch (e) {
                  setNoticeKind('warn')
                  setNotice(`取消归档失败：${e instanceof Error ? e.message : String(e)}`)
                }
              })()
            }}
          >
            取消归档
          </button>
        </div>
      )}
      {boardMenu !== null && (
        <>
          <div className="col-menu-backdrop" onClick={() => setBoardMenu(null)} />
          <div
            className="board-menu"
            style={{ left: boardMenu.x, top: boardMenu.y }}
            onMouseOver={onBoardMenuHover}
          >
            {/* M8：看板选项 —— 目前只做改名 */}
            <button
              type="button"
              className="col-menu__item"
              disabled={busy || readOnly}
              title={readOnly ? '看板已归档，不能改名' : undefined}
              onClick={() => {
                setBoardMenu(null)
                void (async () => {
                  const next = await askPrompt('重命名看板', data!.board.name, '保存')
                  if (next === null) return
                  const trimmed = next.trim()
                  if (trimmed === '') {
                    setNoticeKind('warn')
                    setNotice('看板名称不能为空')
                    return
                  }
                  // 与后端一致：最多 80 个字符
                  const finalName = clampBoardName(trimmed)
                  if (finalName === data!.board.name) return
                  try {
                    await renameBoard(data!.board.id, data!.board.rev, finalName)
                    await load()
                    setNoticeKind('ok')
                    setNotice(`已改名为「${finalName}」`)
                  } catch (err) {
                    setNoticeKind('warn')
                    setNotice(`改名失败：${err instanceof Error ? err.message : String(err)}`)
                  }
                })()
              }}
            >
              看板选项
            </button>
            <button
              type="button"
              className="col-menu__item"
              disabled={busy || readOnly}
              onClick={() => { setBoardMenu(null); setSettingsOpen(true) }}
            >
              显示设置
            </button>
              <div className="mi__sep" />
              <div className="col-menu__block">
                <div className="col-menu__block-title">列对齐（默认，列设置可覆盖）</div>
                <div className="align-row">
                  {([
                    ['left', '左'],
                    ['center', '中'],
                    ['right', '右'],
                  ] as const).map(([v, label]) => (
                    <button
                      key={v}
                      type="button"
                      className={'align-btn' + ((data?.uiSettings?.columnAlign ?? 'left') === v ? ' is-on' : '')}
                      disabled={busy}
                      title={label}
                      aria-label={label}
                      onClick={() => {
                        setBoardMenu(null)
                        if ((data?.uiSettings?.columnAlign ?? 'left') !== v) changeGlobalAlign(v)
                      }}
                    >
                      <AlignIcon kind={v} />
                    </button>
                  ))}
                </div>
              </div>
            <div className="mi__sep" />
              <div className="mi__sep" />
              <button
                type="button"
                className="col-menu__item"
                onClick={() => {
                  setBoardMenu(null)
                  setBoardSub(null)
                  setColCenterOpen(true)
                }}
              >
                列中心…
              </button>
              {/* M10：导入 / 导出改成二级菜单，二级里选格式 */}
              <button
                type="button"
                className="col-menu__item" data-sub="1" onMouseEnter={openBoardSub('import')}
                disabled={busy || readOnly}
                onClick={openBoardSub('import')}
              >
                导入数据到本看板 ▸
              </button>
              <button
                type="button"
                className="col-menu__item" data-sub="1" onMouseEnter={openBoardSub('export')}
                disabled={busy}
                onClick={openBoardSub('export')}
              >
                导出本看板 ▸
              </button>
              {/* 二级面板：导入 */}
              {/* 导出：左右浮窗式二级菜单（对齐「移动到 ›」的做法） */}
              {boardSub?.kind === 'export' && (
                <div className="col-menu col-menu--sub" style={{ left: boardSub.x, top: boardSub.y }}>
                  <div className="row-menu__head">导出格式</div>
                  <button type="button" className="col-menu__item" onClick={() => { setBoardMenu(null); setBoardSub(null); downloadText(`${safeFileName(data!.board.name)}.csv`, 'text/csv', boardToCsv(data!)); setNoticeKind('ok'); setNotice('已导出 CSV') }}>CSV</button>
                  <button type="button" className="col-menu__item" onClick={() => { setBoardMenu(null); setBoardSub(null); downloadText(`${safeFileName(data!.board.name)}.json`, 'application/json', boardToJson(data!)); setNoticeKind('ok'); setNotice('已导出 JSON') }}>JSON（可再导入）</button>
                  <button
      type="button"
      className="col-menu__item"
      onClick={() => {
        setBoardMenu(null)
        setBoardSub(null)
        void (async () => {
          try {
            const blob = await boardToXlsxBlob(data!)
            downloadBlob(`${safeFileName(data!.board.name)}.xlsx`, blob)
            setNoticeKind('ok')
            setNotice('已导出 XLSX')
          } catch (e) {
            setNoticeKind('warn')
            setNotice(`导出 XLSX 失败：${e instanceof Error ? e.message : String(e)}`)
          }
        })()
      }}
    >
      XLSX
    </button>
                </div>
              )}
              {/* 导入：同上 */}
              {boardSub?.kind === 'import' && (
                <div className="col-menu col-menu--sub" style={{ left: boardSub.x, top: boardSub.y }}>
                  <div className="row-menu__head">导入到本看板</div>
                  <button type="button" className="col-menu__item" onClick={() => importRef.current?.click()}>JSON（Friday 导出）</button>
                  <button type="button" className="col-menu__item" onClick={() => xlsxRef.current?.click()}>monday.com XLSX</button>
                </div>
              )}
            <button
              type="button"
              className="col-menu__item"
              disabled={busy || readOnly}
              onClick={() => {
                setBoardMenu(null)
                void (async () => {
                  const okA = await askConfirm(
                    '复制看板？',
                    '将把分组、列（含标签与颜色）、条目与单元格值完整复制成一个新看板，名称加「（副本）」。',
                    '复制',
                  )
                  if (!okA) return
                  try {
                    const res = await copyBoard(data!.board.id)
                    navigate(`/boards/${res.boardId}`)
                  } catch (err) {
                    setNoticeKind('warn')
                    setNotice(`复制失败：${err instanceof Error ? err.message : String(err)}`)
                  }
                })()
              }}
            >
              复制看板
            </button>
            <div className="mi__sep" />
            <button
              type="button"
              className="col-menu__item"
              disabled={busy || readOnly}
              onClick={() => {
                setBoardMenu(null)
                void (async () => {
                  const okA = await askConfirm(
                    '归档看板？',
                    `「${data!.board.name}」将被归档：仍保留在库里，但从看板列表隐藏。可随时取消归档。`,
                    '归档',
                  )
                  if (!okA) return
                  try {
                    await archiveBoard(data!.board.id, data!.board.rev, true)
                    navigate('/')
                  } catch (err) {
                    setNoticeKind('warn')
                    setNotice(`归档失败：${err instanceof Error ? err.message : String(err)}`)
                  }
                })()
              }}
            >
              归档看板
            </button>
            <button
              type="button"
              className="col-menu__item"
              disabled={busy || readOnly}
              onClick={() => { setBoardMenu(null); setArchiveOpen(true) }}
            >
              查看归档
            </button>
            {/* M8：删除看板 —— 需输入看板名确认，不可恢复 */}
            <button
              type="button"
              className="col-menu__item col-menu__item--danger"
              disabled={busy || readOnly}
              title={readOnly ? '看板已归档，请先取消归档再删除' : undefined}
              onClick={() => {
                setBoardMenu(null)
                void (async () => {
                  const typed = await askPrompt(
                    `删除看板「${data!.board.name}」？请原样输入看板名称确认`,
                    '',
                    '删除',
                  )
                  if (typed === null) return
                  if (typed.trim() !== data!.board.name) {
                    setNoticeKind('warn')
                    setNotice('看板名称不一致，已取消删除')
                    return
                  }
                  try {
                    await deleteBoard(data!.board.id, data!.board.rev)
                    navigate('/')
                  } catch (err) {
                    setNoticeKind('warn')
                    setNotice(`删除失败：${err instanceof Error ? err.message : String(err)}`)
                  }
                })()
              }}
            >
              删除看板
            </button>
          </div>
        </>
      )}


      {data?.board.description !== undefined && data.board.description !== '' && (
        <p className="page__note">{data.board.description}</p>
      )}

      {notice !== '' && (
        <p className={noticeKind === 'warn' ? 'notice notice--warn' : 'notice'}>{notice}</p>
      )}

      {loading && <p className="dim">加载中…</p>}
      {error !== '' && <pre className="kv kv--err">{error}</pre>}

      {data !== null && data.columns.length === 0 && <p className="dim">这个看板还没有列。</p>}

      {data?.groups.map((g) => (
        <GroupBlock
            groupDrop={groupDrop}
            onSetGroupDrop={setGroupDrop}
            compact={draggingGroup !== null}
            dragDy={draggingGroup !== null && g.id === draggingGroup.id ? dragDy : 0}
            shift={groupShift(data === null ? -1 : data.groups.findIndex((x) => x.id === g.id))}
            lift={draggingGroup !== null && g.id === draggingGroup.id ? dragLift : 0}
            onDragStartGroup={(info) => {
              dragStartYRef.current = info.startY
              preDragDocHeightRef.current = document.documentElement.scrollHeight
              // 全组折叠：pad 撑住文档高度；lift 用于把源组推回原位 + 折叠后回滚视口
              const secs = [...document.querySelectorAll<HTMLElement>('[data-group-id]')]
              const heights = secs.map((el) => el.getBoundingClientRect().height)
              const collapsed = info.barHeight
              setDragPad(heights.reduce((sum, h) => sum + Math.max(0, h - collapsed), 0))
              const srcIdx = secs.findIndex((el) => Number(el.dataset.groupId) === info.id)
              setDragLift(
                srcIdx <= 0
                  ? 0
                  : heights.slice(0, srcIdx).reduce((sum, h) => sum + Math.max(0, h - collapsed), 0),
              )
              dragStartScrollRef.current = window.scrollY
              lastPointerYRef.current = info.startY
              const gapRaw = getComputedStyle(document.documentElement).getPropertyValue('--group-gap')
              const gap = Number.parseFloat(gapRaw)
              setDragStep(info.barHeight + (Number.isFinite(gap) ? gap : 16))
              setDragDy(0)
              setDraggingGroup(info)
            }}
            onDragEndGroup={() => {
              setDraggingGroup(null)
              setDragDy(0)
            }}
            pinnedCount={pinnedCount}
            onArchiveItem={(id) => void doArchiveItem(id)}
          key={g.id}
          gId={g.id}
          group={g}
          columns={data.columns}
          items={data.items.filter((it) => it.groupId === g.id)}
          editing={editing}
          busy={busy || readOnly}
          onStartEdit={(itemId, columnKey) => setEditing({ itemId, columnKey })}
          onCommit={commit}
          onCancel={() => setEditing(null)}
          onDropItem={dropOnItem}
          onDropColumn={dropOnColumn}
            onDropGroup={() => dropGroupAtPointer()}
          onCreateItem={(name) => doCreateItem(g.id, name)}
          onDeleteItem={doDeleteItem}
          colOps={{
            rename: (id, title) =>
              void runStructure('已重命名列', () => updateColumn(data!.board.id, data!.board.rev, id, { title })),
            remove: (id) =>
              void runStructure('已删除列', () => deleteColumn(data!.board.id, data!.board.rev, id)),
            resize: (id, width) =>
              void runStructure('已调整列宽', () => updateColumn(data!.board.id, data!.board.rev, id, { width })),
            duplicate: (id) => duplicateColumn(id),
            insertRight: (id) => void insertColumnRight(id),
            changeType: (id, cur) => void changeColumnType(id, cur),
            editLabels: (id) => setLabelsFor(id),
  editFormula: (id) => setFormulaFor(id),
  editProgress: (id) => setProgressFor(id),
  editNumFormat: (id) => setNumFormatFor(id),
  setAlign: (id, align) => void changeColumnAlign(id, align),
          }}
          selected={selected}
          footerStatMode={footerAgg}
          onCycleAgg={cycleFooterAgg}
          uiSettings={data?.uiSettings ?? {}}
          onToggleSel={toggleSel}
          onToggleGroup={toggleGroupSel}
          onOpenItem={(iid) => navigate(`/boards/${id}/pulses/${iid}`)}
          onOpenItemNewTab={(iid) => window.open(`/boards/${id}/pulses/${iid}`, '_blank')}
          onCopyLink={(iid) => {
            const url = `${window.location.origin}/boards/${id}/pulses/${iid}`
            void navigator.clipboard.writeText(url).then(
              () => { setNoticeKind('ok'); setNotice('已复制条目链接') },
              () => { setNoticeKind('warn'); setNotice(url) },
            )
          }}
          onDuplicateItem={(id) =>
            void runStructure('已复制条目', () => duplicateItem(data!.board.id, data!.board.rev, id))}
          onCreateBelow={(id) => {
            void (async () => {
              const name = await askPrompt('在下方新建条目', '', '创建')
              if (name === null || name.trim() === '') return
              await runStructure('已新建条目', async () => {
                const created = await createItemBelow(data!.board.id, data!.board.rev, g.id, name.trim(), id)
                // M8：应用默认条目取值
                let rv = created.rev
                const d2 = readItemDefaults(data!.board.viewState)
                for (const [k, v] of Object.entries(d2)) {
                  if (v === '') continue
                  const pr = await patchCell(data!.board.id, created.itemId, k, v, rv)
                  rv = pr.rev
                }
                return created
              })
            })()
          }}
          onMoveItem={(id, toGroupId) =>
            void runStructure('已移动条目', () => moveItem(data!.board.id, data!.board.rev, id, toGroupId))}
          otherGroups={(data === null ? [] : data.groups).map((g) => ({ id: g.id, title: g.title }))}
          grpOps={{
            rename: (title) =>
              void runStructure('已重命名分组', () => updateGroup(data!.board.id, data!.board.rev, g.id, { title })),
            remove: () =>
              void runStructure('已删除分组', () => deleteGroup(data!.board.id, data!.board.rev, g.id)),
            export: () => exportGroupCsv(g.id),
            duplicate: () =>
              void runStructure('已复制分组', () => duplicateGroup(data!.board.id, data!.board.rev, g.id)),
            recolor: (color) =>
              void runStructure('已改分组颜色', () => updateGroup(data!.board.id, data!.board.rev, g.id, { color })),
            toggleCollapse: () =>
              void runStructure(g.collapsed ? '已展开分组' : '已折叠分组', () =>
                updateGroup(data!.board.id, data!.board.rev, g.id, { collapsed: !g.collapsed }),
              ),
          }}
          onDropToGroup={() => dropToGroup(g.id)}
        />
      ))}

      <DialogHost />

      {/* M4：批量操作条（底部居中浮层，对齐 spec §9.2） */}
      {selected.size > 0 && (
        <div className="bulk-bar">
          <span className="bulk-bar__count" aria-label={`${selected.size}`}>
            {selected.size}
          </span>
          <span className="bulk-bar__title">项已选</span>
          <button type="button" className="bulk-bar__item" disabled={busy || readOnly} onClick={() => void bulkDuplicate()}>
            复制
          </button>
          <button type="button" className="bulk-bar__item" onClick={bulkExport}>
            导出 CSV
          </button>
          <button type="button" className="bulk-bar__item bulk-bar__item--danger" disabled={busy || readOnly} onClick={() => void bulkDelete()}>
            删除
          </button>
          <button type="button" className="bulk-bar__item" disabled={busy || readOnly} onClick={() => void bulkMove()}>
            移动到
          </button>
          <button type="button" className="bulk-bar__close" title="清除选择" onClick={() => setSelected(new Set())}>
            ✕
          </button>
        </div>
      )}


      {data !== null && (
        <button
          type="button"
          className="add-group-btn"
          disabled={busy || readOnly}
          onClick={async () => {
            const title = await askPrompt('新建分组标题', '新分组', '创建')
            if (title !== null && title.trim() !== '') {
              const t = title.trim()
              // 新组默认挑一个还没用过的色名，避免几个新组颜色完全一样
              const color = nextGroupColor(data.groups.map((x) => x.color))
              void runStructure('已新建分组', () => createGroup(data.board.id, data.board.rev, t, color))
            }
          }}
        >
          + 新建分组
        </button>
      )}

      {data !== null && data.groups.length === 0 && <p className="dim">这个看板还没有分组。</p>}

      {data !== null && (
        <p className="page__foot">
          条目 {data.items.length} 个 · 列 {data.columns.length} 个 · 分组 {data.groups.length} 个 ·
          点击单元格可编辑
        </p>
      )}
      {archiveOpen && data !== null && (
        <ArchivePanel
          boardId={data.board.id}
          rev={data.board.rev}
          busy={busy || readOnly}
          onClose={() => setArchiveOpen(false)}
          onChanged={() => { void load() }}
        />
      )}
      {numFormatFor !== null && data !== null && (() => {
        const col = data.columns.find((x) => x.id === numFormatFor)
        if (col === undefined) return null
        return (
          <NumberFormatDialog
            column={col}
            busy={busy}
            onCancel={() => setNumFormatFor(null)}
            onSave={(json) => {
              const target = col.id
              setNumFormatFor(null)
              void runStructure('已更新显示格式', () =>
                updateColumn(data!.board.id, data!.board.rev, target, { settings: json }),
              )
            }}
          />
        )
      })()}

      {progressFor !== null && data !== null && (() => {
        const pcol = data.columns.find((x) => x.id === progressFor)
        if (pcol === undefined) return null
        return (
          <ProgressSettings
            column={pcol}
            columns={data.columns}
            busy={busy || readOnly}
            onCancel={() => setProgressFor(null)}
            onSave={(settingsJson) => {
              setProgressFor(null)
              void (async () => {
                try {
                  await updateColumn(data.board.id, data.board.rev, pcol.id, { settings: settingsJson })
                  await load()
                  setNoticeKind('ok')
                  setNotice('已保存进度设置')
                } catch (e) {
                  setNoticeKind('warn')
                  setNotice(`保存进度设置失败：${e instanceof Error ? e.message : String(e)}`)
                }
              })()
            }}
          />
        )
      })()}
      {formulaFor !== null && data !== null && (() => {
        const fcol = data.columns.find((x) => x.id === formulaFor)
        if (fcol === undefined) return null
        return (
          <FormulaEditor
            column={fcol}
            columns={data.columns}
            busy={busy || readOnly}
            onCancel={() => setFormulaFor(null)}
            onSave={(settingsJson) => {
              setFormulaFor(null)
              void (async () => {
                try {
                  await updateColumn(data.board.id, data.board.rev, fcol.id, { settings: settingsJson })
                  await load()
                  setNoticeKind('ok')
                  setNotice('已保存公式')
                } catch (e) {
                  setNoticeKind('warn')
                  setNotice(`保存公式失败：${e instanceof Error ? e.message : String(e)}`)
                }
              })()
            }}
          />
        )
      })()}
      {settingsOpen && data !== null && (() => {
        let vs: Record<string, unknown> = {}
        try {
          const raw = data.board.viewState
          const parsed: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
          if (parsed !== null && typeof parsed === 'object') vs = parsed as Record<string, unknown>
        } catch {
          vs = {}
        }
        return (
          <BoardSettingsPanel
            itemHeight={typeof vs.ItemHeight === 'number' ? vs.ItemHeight : 36}
            headerHeight={typeof vs.HeaderHeight === 'number' ? vs.HeaderHeight : 36}
            pinned={typeof vs.PinnedColumns === 'number' ? vs.PinnedColumns : 0}
              groupGap={groupGap}
            columns={data.columns}
            defaults={readItemDefaults(data.board.viewState)}
            busy={busy || readOnly}
            onCancel={() => setSettingsOpen(false)}
            onSave={(patch) => {
              setSettingsOpen(false)
              if (Object.keys(patch).length === 0) return
              void (async () => {
                try {
                  await setBoardAppSetting(data.board.id, 'groupGap', String(patch.GroupGap ?? groupGap))
                    await patchBoardSettings(data.board.id, data.board.rev, patch)
                  await load()
                  setNoticeKind('ok')
                  setNotice('已保存显示设置')
                } catch (e) {
                  setNoticeKind('warn')
                  setNotice(`保存显示设置失败：${e instanceof Error ? e.message : String(e)}`)
                }
              })()
            }}
          />
        )
      })()}      {labelsFor !== null && data !== null && (() => {
        const col = data.columns.find((x) => x.id === labelsFor)
        if (col === undefined) return null
        return (
          <LabelsEditor
            column={col}
            busy={busy || readOnly}
            onCancel={() => setLabelsFor(null)}
            onSave={(labelsJson, settingsJson, plan) => {
              setLabelsFor(null)
              void (async () => {
                try {
                  const res = await updateColumnLabels(
                    data.board.id, data.board.rev, col.id, labelsJson, settingsJson, plan,
                  )
                  await load()
                  setNoticeKind('ok')
                  setNotice(
                    res.deleted > 0 || res.moved > 0
                      ? `已保存标签（改写 ${res.moved} 条、清除 ${res.deleted} 条相关值）`
                      : '已保存标签',
                  )
                } catch (e) {
                  setNoticeKind('warn')
                  setNotice(`保存标签失败：${e instanceof Error ? e.message : String(e)}`)
                }
              })()
            }}
          />
        )
      })()}      {colCenterOpen && data !== null && (
        <ColumnCenter
          columns={data.columns}
          uiSettings={data.uiSettings ?? {}}
          busy={busy || readOnly}
          onClose={() => setColCenterOpen(false)}
          onDelete={(ids) => batchDeleteColumns(ids)}
          onChangeType={(ids, type) => batchChangeColumnType(ids, type)}
          onAlign={(ids, align) => batchAlignColumns(ids, align)}
          onWidth={(ids, width) => batchResizeColumns(ids, width)}
        />
      )}

      {detailItem !== null && data !== null && (
        <ItemDetailPanel
          item={detailItem}
          columns={data.columns}
          hasPrev={detailIdx > 0}
          hasNext={detailIdx < allItems.length - 1}
          onPrev={() => {
            const prev = allItems[detailIdx - 1]
            if (prev !== undefined) navigate(`/boards/${id}/pulses/${prev.id}`)
          }}
          onNext={() => {
            const next = allItems[detailIdx + 1]
            if (next !== undefined) navigate(`/boards/${id}/pulses/${next.id}`)
          }}
          editingKey={detailEditing}
          busy={busy || readOnly}
          onStartEdit={(ck) => setDetailEditing(ck)}
          onCancelEdit={() => setDetailEditing(null)}
          onCommitCell={(ck, v) => {
            setDetailEditing(null)
            commit(detailItem.id, ck, v)
          }}
          onDelete={() => {
            void (async () => {
              const okDel = await askConfirm('删除条目？', `删除条目「${detailItem.name}」？此操作不可撤销。`, '删除')
              if (!okDel) return
              setDetailEditing(null)
              await runStructure('已删除条目', () =>
                // 注意参数顺序：deleteItem(boardId, itemId, rev) —— rev 在最后（与 patchCell 一致）
                deleteItem(data!.board.id, detailItem.id, data!.board.rev),
              )
              navigate(`/boards/${id}`)
            })()
          }}          onClose={() => {
            setDetailEditing(null)
            navigate(`/boards/${id}`)
          }}
        />
      )}
    </div>
  )
}

/** 页面级兜底：整页渲染出错时给一个可读的提示，而不是纯黑 */
export function BoardPageWithBoundary() {
  return (
    <ErrorBoundary
      label="BoardPage"
      fallback={
        <div className="page">
          <div className="page__head">
            <h1 className="page__title">看板渲染失败</h1>
          </div>
          <p className="card__hint">
            这一页里某个组件抛错了。请刷新重试；如果一直失败，把当前看板 id 告诉我。
          </p>
        </div>
      }
    >
      <BoardPage />
    </ErrorBoundary>
  )
}
